/* eslint-disable */

/**
 * Tx Presenter Vuex module — Slice 1
 *
 * Wires up the 4 Slice 1 backend endpoints:
 *   - GET dashboard (/get-tx-presenter-dashboard)
 *   - GET drawer detail (/get-tx-presentation-detail)
 *   - SAVE internal note (/save-tx-internal-note)
 *
 * Designed to be reused by both admin (TC) flows and the patient at-home view
 * per Frontend Architecture decision (Apr 29). Patient context will pass a
 * short URL token instead of relying on admin auth.
 */

import axios from 'axios';
import Vue from 'vue';
import { TX_PRESENTER_DEFAULTS } from '@/pages/TxPresenter/constants/settingsDefaults.js';
import { isDiagnosticCode } from '@/pages/TxPresenter/utils/procedureOrder.js';
import { resolveSiteChannelsFromSettings } from '@/pages/TxPresenter/utils/resolveChannels.js';
import { normalizeDisclaimer } from '@/pages/TxPresenter/utils/disclaimer.js';
import { extractApiError } from '@/utils/apiError';
import { getIdToken as getFirebaseIdToken } from '@/pages/MyAccount/components/firebaseIdToken';
// Photo Attachments — the same shared uploader Forms, Wiki, and Chat use.
// Brings the image allowlist, the 10MB cap, and client-side compression with
// it; we only override the endpoint so the key lands under tx-photos/.
import { uploadToS3 } from '@/utils/fileUpload';
// Backstop for the same cap the backend enforces. Checked here so the modal can
// say something useful instead of surfacing a generic upload failure.
const MAX_PATIENT_PHOTOS = 100;
import { diffProcedureSets, mergeNewProcNums } from '@/pages/TxPresenter/utils/refreshDiff.js';
import {
  extractCodeOverrides,
  resolveProcLabel,
  resolveProcReason,
  applyCodeOverride,
  findLegacyConflicts,
} from '@/pages/TxPresenter/utils/resolveProcContent.js';

// Note: axios.defaults.baseURL is already set globally in main.js

// /generate-procedure-content verifies the caller's Firebase ID token and that the token's siteID
// matches the body's (M-AI2). getFirebaseIdToken never throws and waits out Firebase's async init.
async function procedureContentConfig(rootState) {
  const idToken = await getFirebaseIdToken(rootState && rootState.getAuth ? rootState.getAuth() : null);
  const headers = {};
  if (idToken) headers.Authorization = `Bearer ${idToken}`;
  return { crossdomain: true, headers };
}

// Latest-wins guard for the dashboard fetch. Live (debounced) search can put
// more than one fetch in flight; a slower earlier response must not overwrite
// (or stop the spinner for) a newer one. Module-scoped counter — fetchDashboard
// captures its seq and only commits if it's still the latest.
let dashboardFetchSeq = 0;

// Serializes saveAnnotations calls so rapid successive saves (e.g., a TC
// flipping several pricingDisplay toggles in a row) run one-at-a-time. Each
// save reads `annotationsUpdatedAt` only after the previous save has committed
// the fresh baseline — eliminating the self-race that produced spurious 409
// "another team member edited this plan" banners. Genuine cross-user conflicts
// still surface. Module-scoped (shared across all dispatches).
let annotationsSaveChain = Promise.resolve();

// Latest-wins guard for fetchCherryPlanTerms: a TC typing a Pay today can put several re-prices
// in flight, and an older (slower) answer must not overwrite the one for the amount now on
// screen. Same pattern as dashboardFetchSeq above.
let cherryTermsFetchSeq = 0;

export default {
  namespaced: true,

  state: {
    // Dashboard
    dashboard: {
      patients: [],
      totalCount: 0,
      fromDate: null,
      toDate: null,
      siteID: null,
      loading: false,
      error: null,
      lastFetchedAt: null,
    },

    // Filters (persist between dashboard fetches)
    filters: {
      windowDays: 30,
      country: 'USA',
      providerNums: [],
      search: '',
      limit: 100,
    },

    // Drawer (per-patient detail)
    drawer: {
      open: false,
      patient: null,
      plans: [],
      selectedTreatPlanNum: null,
      selectedPlan: null,
      internalNote: null,
      timeline: [],
      siteID: null,
      loading: false,
      saving: false, // saving the internal note
      error: null,
    },

    // Patient view (Slice 7) — public payload from /get-tx-patient-view
    // Used by TxPatientView.vue (no admin auth; only the shortId in the URL).
    patientView: {
      shortId: null,
      // { siteID, patNum, treatPlanNum } resolved server-side from the shortId.
      // Declared here (not just set in the mutation) so it's reactive in Vue 2 —
      // the payment path reads these via the resolved()/patNum/siteID computeds.
      resolved: null,
      practice: null,        // { name, tagline }
      patient: null,         // { fName }
      plan: null,            // { treatPlanNum, heading, isActive, dateTP }
      procedures: [],
      altsByPrimary: {},
      procedureContent: { labels: {}, reasons: {} },
      overrides: { labels: {}, reasons: {} },
      paymentOptions: null,
      // Cherry's REAL plans for the plan's financing option, as the BACKEND stored them:
      // { purchaseAmount, plans: [...], fetchedAt } or null. This page is public and cannot
      // price anything itself, so absent from the payload = null = the card shows no figures.
      cherryTerms: null,
      // Practice-wide insurance disclaimer + acknowledgement lines, resolved by the
      // BACKEND (resolvePatientDisclaimer output) or null when the office has none.
      // This page is public and never sees the raw settings blob.
      disclaimer: null,
      pricingDisplay: null,    // Two-level pricing config from Builder
      viewConfig: null,        // { enabledViews: [...], defaultView } from practice settings
      preferences: { selectedIds: [], otherNote: '', updatedAt: null },  // Slice 7c
      latestSignature: null, // Slice 7d — { pdfId, signedAt, signerName } or null
      consentForms: [],      // Slice 11 Phase B — [{formIDForWeb, formName, shortUrl}]
      // Photo Attachments — fetched separately from /get-tx-patient-photos, not
      // part of the main payload, because the S3 urls are presigned and expire
      // while this bundle lives in the store for the life of the tab.
      // `photosEnabled` is the SERVER's answer on the showPhotos flag; trust it
      // over pricingDisplay.showPhotos, since the server signs nothing when off.
      photos: [],
      photosEnabled: false,
      photosLoading: false,
      // Per-site capability from the server (this page can't read teamio.settings).
      // Both default-deny until the payload says otherwise.
      paymentsEnabled: false,
      officeCanNotify: false,
      totalFee: 0,
      // Guarantor account balance / credit (BalTotal − InsEst) from OD, folded
      // into the Estimated Patient Portion when the showBalance toggle is on.
      familyBalance: 0,
      // "Pay toward balance" ledger (Slice 2): paid payments, sum, and remaining.
      payments: [],
      paidTotal: 0,
      remaining: 0,
      loading: false,
      savingPreferences: false,  // Slice 7c — auto-save indicator
      error: null,
    },

    // Slice 11 Phase C — Patient Profile "Tx Plans" dispatcher.
    // Lives separately from `drawer` (the Slice 1 dashboard drawer) so the two
    // entry points don't pollute each other's state. Just enough to render a
    // plan picker modal and hand off to SendPlanModal.
    patientTxPlansDispatcher: {
      open: false,
      siteID: null,
      patient: null,    // { fName, lName, wirelessPhone, email, ... }
      plans: [],        // [{ treatPlanNum, heading, status, dateTP }]
      loading: false,
      error: null,
    },

    // Photo Attachments — the office-side view of a PATIENT's photo library.
    //
    // Patient-level, not plan-level: the same list serves every saved plan for
    // that patient, which is why `loadedFor` is keyed on siteID+patNum and not
    // on treatPlanNum. Walking Builder → Present Mode for the same patient must
    // not refetch, and switching plans must not either.
    patientPhotos: {
      list: [],          // [{ photoId, teeth: [Number], fileName, url, ... }]
      loadedFor: null,   // `${siteID}:${patNum}` — null = never loaded
      loading: false,
      error: null,
    },

    // Patient Profile "Treatment Plans Signed" summary.
    // Read-only list of the patient's plans signed via Tx Presenter, each with
    // the consent forms bundled with that plan and their signed/unsigned state.
    // Fed by /get-tx-signed-plans-summary; non-fatal (empty on error) so a
    // backend hiccup never blocks the Patient Profile render.
    signedPlansSummary: {
      loading: false,
      plans: [],   // sorted latest-signed first (backend orders it)
    },

    // Send-plan modal (Slice 7a) — short URL state for the Builder's Send action
    sendPlan: {
      open: false,
      shortId: null,
      fullUrl: null,
      reused: false,
      generating: false,
      error: null,
      // Slice 11 Phase B — persisted bundled formIDs for this plan, hydrated
      // by openSendPlan from the backend. SendPlanModal seeds its toggle from
      // this so reopening the modal reflects what's actually saved (instead
      // of always defaulting to OFF and confusing TCs).
      persistedBundledFormIDs: [],
    },

    // Sign flow (Slice 7d) — patient-side signing state.
    // After the @react-pdf migration the stages collapsed to just
    // submitting → done, since the backend renders + emails async and the
    // patient never has to wait on the heavy work.
    sign: {
      submitting: false,
      stage: 'idle',     // 'idle' | 'submitting' | 'done' | 'error'
      result: null,      // { pdfId, signedAt, status: 'processing', dispatch: {...} }
      error: null,
    },

    // Comments / Q&A thread (Slice 8) — separate buckets per surface so the
    // Builder thread state can't clobber the patient view thread state when
    // a TC has both contexts open in different tabs. Each holds the same
    // shape: comments[] sorted by CommentID asc, latestCommentID for polling,
    // ui state for the panel.
    threadBuilder: {
      comments: [],
      latestCommentID: 0,
      loading: false,
      polling: false,
      error: null,
      panelOpen: false,            // 8a: Builder Comments overlay
      // Identity of which (siteID,patNum,treatPlanNum) thread is loaded so the
      // store knows whether to reset when the Builder switches plans.
      siteID: null,
      patNum: null,
      treatPlanNum: null,

      // Slice 8c — TC compose + AI override state. composing.replacing holds
      // the AI commentID being overridden when the TC clicked "Replace this
      // reply" on an AI row; null when posting a fresh follow-up.
      composing: {
        sending: false,
        error: null,
        draft: '',                   // pre-fill text when entering replace mode
        replacing: null,             // commentID of the AI row being replaced (null = plain reply)
        pendingId: 0,                // optimistic placeholder ID
        // Slice 8 UX — commentID currently being released via releaseAiReply.
        // Drives the spinner + disabled state on that row's Approve button so
        // a panicky TC doesn't fire two releases in a row.
        approvingCommentID: null,
      },
    },
    threadPatient: {
      comments: [],
      latestCommentID: 0,
      loading: false,
      polling: false,
      error: null,
      // Patient view doesn't need siteID etc — it's tied to the active shortId
      // already on patientView.shortId.

      // MASTER gate for the whole conversation section on the patient page,
      // resolved server-side into getTxPatientView's thread.commentsEnabled
      // (stored `commentsEnabled === true` AND a verified sender email).
      //
      // Starts FALSE and stays false unless the payload explicitly says true —
      // this page is public and has no customer.settings of its own, so the flag
      // cannot be derived client-side. Default-deny matches paymentsEnabled /
      // officeCanNotify; see SET_PATIENT_VIEW_DATA.
      commentsEnabled: false,
      // Slice 8b — patient compose state. Resolved from getTxPatientView's
      // thread.aiCommentsEnabled (AND of platform + per-practice gates) so the
      // disclosure banner + "Office is replying..." indicator only show when
      // AI is actually going to fire. SUB-flag: only meaningful when
      // commentsEnabled is true.
      aiCommentsEnabled: true,
      // Slice 8d-1 — 'auto' (default) shows "Office is replying…" placeholder;
      // 'review' skips it because the AI draft is held for office approval.
      aiReplyMode: 'auto',
      composing: {
        sending: false,        // POST in flight
        error: null,           // last submit error message
        pendingPatientId: 0,   // negative placeholder ID (gets replaced on success)
        pendingAiId: 0,        // 0 if AI gates off OR mode='review'; otherwise negative placeholder
      },
    },

    // Settings (Slice 5)
    // Per-section state. `null` value = practice hasn't saved that section yet
    // (frontend falls back to TX_PRESENTER_DEFAULTS until first save).
    settings: {
      siteID: null,
      sections: {
        general: null,
        presentation: null,
        payment: null,
        comments: null,
        delivery: null,
        procedure_content: null,
        consent: null,
        addons: null,
      },
      loading: false,
      savingSection: null,   // 'general' | 'payment' | etc. when a save is in flight
      error: null,
      lastFetchedAt: null,
    },

    // Builder (Slice 2 — 3-pane workspace)
    // saveStatus: 'idle' | 'saving' | 'saved' | 'error' (Google-Docs–style indicator)
    builder: {
      patNum: null,
      treatPlanNum: null,
      siteID: null,
      plan: null,
      procedures: [],
      proceduresOrder: null,    // persisted custom order [ProcNum, ...] or null = OD default
      annotations: {},          // { [procNum]: { isHiddenByPriority: bool, shortNameOverride, reasonOverride, ... } }
      // Per-patient payment-option overrides. Null = practice defaults
      // are in force; an array (full resolved-options shape) = the TC has
      // customized this patient's options in the right-pane "Payment
      // options for this patient" section. Persisted in
      // tx_presenter_presentations.PaymentOptionOverrides JSON column.
      paymentOptionOverrides: null,
      // Cherry's REAL plans for the current plan's Cherry-backed option:
      // { purchaseAmount, plans: [...], fetchedAt } or null. Priced for ONE amount - callers
      // must check cherryTermsMatch before showing a figure. Written by fetchCherryPlanTerms
      // only (the backend stores the same terms for the patient view and the signed PDF).
      // Reset with the rest of the plan state; a failed fetch leaves it null (=> no figures).
      cherryTerms: null,
      cherryTermsLoading: false,
      priorities: [],           // [{ defNum, itemName, itemOrder }, ...]
      // Which priority DefNums the TC hid. PERSISTED plan-level as
      // annotations.hiddenPriorities and rehydrated in SET_BUILDER_DATA, so the
      // selection reaches Present mode and the tablet (a separate session).
      hiddenPriorityDefNums: [],
      groupBy: 'priority',      // 'priority' | 'phase' | 'quadrant' | 'tooth' | 'none'
      // NOT a freshness signal. Stamped client-side in SET_BUILDER_DATA — it
      // means "when this browser last fetched", not "how current the mirror
      // is". It will read "just now" even if the site's exe is stalled and a
      // procedure added in OD hasn't replicated yet, which is why every label
      // built on it says "checked", never "synced".
      lastSyncAt: null,
      // Refresh-from-OD results. `refreshOutcome` drives the top-bar pill's
      // transient result text; `newProcNums` drives the New badge in the left
      // pane and survives until the TC acknowledges the row.
      // Written by refreshBuilderFromOd ONLY — never by loadBuilder, or every
      // procedure would read as new on first paint.
      refreshOutcome: null,     // { added: [procNum], removed: [procNum], at } | null
      newProcNums: [],
      totalFee: 0,
      totalNetFee: 0,
      // Guarantor account balance / credit (BalTotal − InsEst) from OD. Positive
      // = owed (adds to portion), negative = credit. Folded into the Estimated
      // Patient Portion by PlanFinancialSummary when the showBalance toggle is on.
      familyBalance: 0,
      // "Pay toward balance" ledger (Slice 3): paid payments + sum for this plan.
      payments: [],
      paidTotal: 0,
      loading: false,
      saveStatus: 'idle',
      error: null,
      // Slice 10a #8 — concurrent-edit conflict detection.
      // `annotationsUpdatedAt`: row's UpdatedAt at last load/save, sent
      // back as `expectedUpdatedAt` on each save. Null = no presentations
      // row exists yet (first edit, no version to clash with).
      // `conflict`: when a 409 response lands, holds the latest persisted
      // proceduresOrder + annotations the other TC wrote, plus a message.
      // UI renders a banner with "Refresh" to drop local edits and adopt
      // the persisted state.
      annotationsUpdatedAt: null,
      conflict: null,
      // Slice 10a #9 — latest signed-PDF row for this plan. Null = plan
      // never signed. Used to drive the "Retry PDF" banner in the Builder
      // when status === 'failed'. `retryingSignedPdf` is a transient flag
      // for the retry action's spinner state.
      latestSignedPdf: null,
      retryingSignedPdf: false,

      // Slice 6 — AI-generated procedure content keyed by CodeNum (NOT procNum,
      // so the cache hits across plans/patients with the same CDT code).
      // Each entry: { content, source: 'cache'|'llm'|'fallback', cached: bool }
      procedureContent: {
        labels: {},    // { [codeNum]: { content, source, cached } }
        reasons: {},   // same
        loadingLabels: false,
        loadingReasons: false,
        regenerating: {},  // { [codeNum + ':' + type]: true } — per-row regen state
        bulkRegenerating: { labels: false, reasons: false },
        error: null,
      },
    },
  },

  getters: {
    /**
     * Which patient-facing messaging channels this site may actually use, resolved
     * from teamio.settings — see pages/TxPresenter/utils/resolveChannels.js and its
     * backend twin. Single source for every Tx Presenter surface that offers to
     * text or email a patient, so the UI can't disagree with what the server will
     * accept.
     *
     * Reads the raw `customer.settings` ARRAY. Deliberately NOT
     * `customer.allSettings` — that property does not exist, and reading it is what
     * made the PAYMENT_ENABLED gate in PresentDepositPanel.vue return false on
     * every site. Getters can't reach the global mixin's `allSettings` computed, so
     * the array is the correct source here.
     *
     * `settings` is loaded at app init; while it's still null this resolves to
     * "nothing available". Callers that gate destructive/hiding UI should pair this
     * with `siteChannelsReady` so an entitled site never flashes a disabled state
     * on a cold load.
     */
    siteChannels: (state, getters, rootState) =>
      resolveSiteChannelsFromSettings(rootState.customer && rootState.customer.settings),

    /** False until customer.settings has hydrated — "unknown", not "denied". */
    siteChannelsReady: (state, getters, rootState) =>
      Array.isArray(rootState.customer && rootState.customer.settings),

    /**
     * Teamio Forms add-on (FORMS_PAID_FOR, 'TRUE'/'FALSE', missing = off).
     * src/config/ability.js already gates the whole Forms module on a strict
     * === 'TRUE' of this key, so the comparison is production-proven; this getter
     * makes it reachable from Tx Presenter, where consent-form bundling is a Forms
     * feature used from a non-Forms page.
     *
     * A getter rather than a mixin computed because the global mixin is NOT applied
     * globally and SendPlanModal doesn't include it — same situation as
     * Forms/EditForm.vue, which reads the raw settings array for AI_FORMS_PAID_FOR
     * for exactly this reason. Reads `customer.settings` (the ARRAY);
     * `customer.allSettings` does not exist.
     *
     * Returns TRUE while settings are still hydrating — see siteChannels: unknown
     * is not denied, and the server default-denies regardless
     * (tx-presenter-service/lib/entitlements.js).
     */
    formsPaidFor: (state, getters, rootState) => {
      const settings = rootState.customer && rootState.customer.settings;
      if (!Array.isArray(settings)) return true;
      const row = settings.find((s) => s && s.Property === 'FORMS_PAID_FOR');
      return !!(row && row.PropertyValue === 'TRUE');
    },

    /**
     * Cherry patient-financing add-on. Same shape as formsPaidFor above, but DEFAULT-DENY while
     * hydrating (changed 2026-10-10): `undefined` settings means "we don't know yet" and this
     * returns false. formsPaidFor fails open because a brief optimistic TRUE there only flashes
     * a tab; here the same optimism would let the Builder and Present Mode call cherry-service
     * for a site that may not be entitled (it default-denies server-side, so the call would
     * 403 and be logged as a failure). Consumers - the Settings connection card and
     * "Financing partner" select, and cherryTermsSync's price-fetching - are all fine waiting
     * the moment it takes customer.settings to load.
     *
     * Note the value convention: 'TRUE'/'FALSE', matching FORMS_PAID_FOR and what
     * SuperAdmin's toggleFeature writes — NOT the '1'/'0' PAYMENT_ENABLED uses.
     */
    cherryEnabled: (state, getters, rootState) => {
      const settings = rootState.customer && rootState.customer.settings;
      if (!Array.isArray(settings)) return false;
      const row = settings.find((s) => s && s.Property === 'CHERRY_ENABLED');
      return !!(row && row.PropertyValue === 'TRUE');
    },

    dashboardPatients: (state) => state.dashboard.patients,
    dashboardLoading: (state) => state.dashboard.loading,
    drawerOpen: (state) => state.drawer.open,
    drawerPatient: (state) => state.drawer.patient,
    drawerSelectedPlan: (state) => state.drawer.selectedPlan,
    drawerPlans: (state) => state.drawer.plans,
    drawerSelectedTreatPlanNum: (state) => state.drawer.selectedTreatPlanNum,

    // Helper — has saved plans?
    drawerHasSavedPlans: (state) =>
      Array.isArray(state.drawer.plans) && state.drawer.plans.some((p) => p.status === 0),

    // Helper — count of saved plans
    drawerSavedPlanCount: (state) =>
      Array.isArray(state.drawer.plans)
        ? state.drawer.plans.filter((p) => p.status === 0).length
        : 0,

    // Builder — visible procedures (after applying hide-by-priority + custom order
    // + filtering out alts that are now nested under a primary).
    builderVisibleProcedures: (state, getters) => {
      const { procedures, proceduresOrder, annotations, hiddenPriorityDefNums } = state.builder;
      if (!Array.isArray(procedures) || procedures.length === 0) return [];

      // 1) Apply persisted custom order if any
      let ordered = procedures;
      if (Array.isArray(proceduresOrder) && proceduresOrder.length > 0) {
        const indexMap = new Map(proceduresOrder.map((id, idx) => [id, idx]));
        ordered = [...procedures].sort((a, b) => {
          const ai = indexMap.has(a.procNum) ? indexMap.get(a.procNum) : Number.MAX_SAFE_INTEGER;
          const bi = indexMap.has(b.procNum) ? indexMap.get(b.procNum) : Number.MAX_SAFE_INTEGER;
          if (ai === bi) return a.procNum - b.procNum;
          return ai - bi;
        });
      }

      // Procs that are alts (non-primary) of some other proc — those render
      // nested under their primary, NOT in the flat list.
      const altProcNums = getters.builderAltProcNumSet;

      // 2) Filter out hidden-by-priority + alts. Conflict rule: explicit attach
      // beats hide — but since alts render under their primary anyway, that's
      // handled implicitly: even if their priority is hidden, they show via
      // builderAltsByPrimary.
      //
      // `hiddenPriorityDefNums` is hydrated in SET_BUILDER_DATA from the
      // PLAN-level annotations.hiddenPriorities, so it now survives into Present
      // mode and onto the tablet (a different browser session). Before
      // 2026-09-10 it was session-local and both Present paths silently showed
      // every hidden group back.
      const hiddenSet = new Set(
        (hiddenPriorityDefNums || []).map((d) => parseInt(d, 10)).filter((d) => Number.isFinite(d))
      );
      // Diagnostic (D0xxx) visibility — per-patient pricingDisplay.showDiagnostic
      // wins; otherwise the practice "house style" default (Settings →
      // Presentation); otherwise shown. When off, exams & x-rays are filtered
      // from the list (and therefore every view + total).
      const pd = (annotations && annotations.pricingDisplay) || {};
      const practiceShowDiag = (((getters.settingsPresentationResolved || {}).displayDefaults) || {}).showDiagnostic;
      const showDiagnostic = Object.prototype.hasOwnProperty.call(pd, 'showDiagnostic')
        ? pd.showDiagnostic !== false
        : practiceShowDiag !== false;
      // ⚠️ TWIN of filterVisibleProcedures in the backend's lib/plan-procs.js.
      // The precedence below must stay identical in both, or this preview and
      // the patient's own page disagree. Pinned by
      // cypress/node-tests/tx-presenter-hidden-priority-parity.test.js, which
      // runs one fixture through both implementations.
      //
      //   1. non-primary alternative      → hidden
      //   2. removed                      → hidden
      //   3. diagnostic while off         → hidden   (frontend-only axis; must
      //      come BEFORE the un-hide escape or an attached D0xxx alt would
      //      reappear with exams switched off)
      //   4. isHiddenByPriority === false → VISIBLE  (explicit un-hide wins)
      //   5. plan-level hiddenPriorities  → hidden
      //   6. isHiddenByPriority truthy    → hidden
      return ordered.filter((p) => {
        if (altProcNums.has(p.procNum)) return false;
        const anno = annotations && annotations[p.procNum];
        if (anno && anno.removed) return false;
        if (!showDiagnostic && isDiagnosticCode(p.procCode)) return false;
        if (anno && anno.isHiddenByPriority === false) return true;
        if (hiddenSet.size > 0 && hiddenSet.has(Number(p.priority))) return false;
        if (anno && anno.isHiddenByPriority) return false;
        return true;
      });
    },

    // Builder — procedures removed from this plan (Teamio-side only; the proc
    // still lives in Open Dental). Same order logic as builderVisibleProcedures.
    // Each entry is the proc object plus removedAt/removedBy pulled from the
    // annotation's `removed: { at, by }`.
    builderRemovedProcedures: (state) => {
      const { procedures, proceduresOrder, annotations } = state.builder;
      if (!Array.isArray(procedures) || procedures.length === 0) return [];
      if (!annotations) return [];

      let ordered = procedures;
      if (Array.isArray(proceduresOrder) && proceduresOrder.length > 0) {
        const indexMap = new Map(proceduresOrder.map((id, idx) => [id, idx]));
        ordered = [...procedures].sort((a, b) => {
          const ai = indexMap.has(a.procNum) ? indexMap.get(a.procNum) : Number.MAX_SAFE_INTEGER;
          const bi = indexMap.has(b.procNum) ? indexMap.get(b.procNum) : Number.MAX_SAFE_INTEGER;
          if (ai === bi) return a.procNum - b.procNum;
          return ai - bi;
        });
      }

      return ordered
        .filter((p) => {
          const a = annotations[p.procNum];
          return !!(a && a.removed);
        })
        .map((p) => {
          const removed = (annotations[p.procNum] && annotations[p.procNum].removed) || {};
          return { ...p, removedAt: removed.at || null, removedBy: removed.by || null };
        });
    },

    // Set of procNums that are alts (i.e., have altGroupId AND isPrimaryInGroup=false).
    // Used to exclude these from the flat top-level list.
    builderAltProcNumSet: (state) => {
      const out = new Set();
      const annos = state.builder.annotations || {};
      Object.keys(annos).forEach((k) => {
        const a = annos[k];
        if (a && a.altGroupId && a.isPrimaryInGroup === false) {
          out.add(parseInt(k, 10));
        }
      });
      return out;
    },

    // { [primaryProcNum]: [altProc, altProc, ...] } — alts rendered nested
    // under their primary in the left pane and views. Sorted by altOrder ASC.
    builderAltsByPrimary: (state) => {
      const out = {};
      const annos = state.builder.annotations || {};
      const procByNum = new Map((state.builder.procedures || []).map((p) => [p.procNum, p]));

      Object.keys(annos).forEach((k) => {
        const a = annos[k];
        if (!a || !a.altGroupId || a.isPrimaryInGroup !== false) return;
        const procNum = parseInt(k, 10);
        const proc = procByNum.get(procNum);
        if (!proc) return;
        const primaryProcNum = parseInt(a.altGroupId, 10);
        if (!out[primaryProcNum]) out[primaryProcNum] = [];
        out[primaryProcNum].push({ ...proc, _altOrder: a.altOrder || 0 });
      });

      Object.keys(out).forEach((k) => {
        out[k].sort((x, y) => (x._altOrder || 0) - (y._altOrder || 0));
      });
      return out;
    },

    // Set of procNums that are already in some alt group (either primary OR alt)
    // — used by the picker to disable already-attached procs.
    builderProceduresInAnyGroup: (state) => {
      const out = new Set();
      const annos = state.builder.annotations || {};
      Object.keys(annos).forEach((k) => {
        const a = annos[k];
        if (a && a.altGroupId) out.add(parseInt(k, 10));
      });
      return out;
    },

    builderHiddenCount: (state, getters) => {
      const total = state.builder.procedures.length;
      // Removed procs are surfaced by the "Removed from this plan" strip, not
      // the "N hidden" meta — subtract them so they aren't double-labelled.
      return total - getters.builderVisibleProcedures.length - getters.builderRemovedProcedures.length;
    },

    // ─── Slice 6 — resolved procedure content ─────────
    // Resolution priority: per-CODE override → AI cache → OD abbrDesc/descript.
    // Overrides moved from per-procedure to per-code on 2026-08-04 so renaming
    // D2740 "Crown" → "Cap" applies to every crown on the plan instead of one
    // tooth at a time. Legacy per-procNum values are honored read-time — see
    // utils/resolveProcContent.js (twinned with the backend).
    //
    // Returned as functions so view components can call e.g.
    //   :label="builderResolvedLabel(proc)"
    builderCodeOverrides: (state) => extractCodeOverrides(
      state.builder.annotations,
      state.builder.procedures
    ),
    builderResolvedLabel: (state, getters) => (proc) => resolveProcLabel(
      proc, getters.builderCodeOverrides, state.builder.procedureContent.labels
    ),
    builderResolvedReason: (state, getters) => (proc) => resolveProcReason(
      proc, getters.builderCodeOverrides, state.builder.procedureContent.reasons
    ),
    // True if this procedure's CODE has a per-plan override on a given field —
    // drives the "overridden" pill. Takes the proc object (not a procNum) since
    // it now needs codeNum. Keys off the MERGED map, so a legacy per-tooth value
    // surfacing through the shim correctly reads as overridden — it is one.
    builderHasOverride: (state, getters) => (proc, field) => {
      const m = field === 'shortNameOverride'
        ? getters.builderCodeOverrides.labels
        : getters.builderCodeOverrides.reasons;
      return !!(proc && m[String(proc.codeNum)]);
    },
    // Codes whose legacy per-tooth overrides disagreed with each other and will
    // collapse to one under per-code overrides. Surfaced in the Builder so the
    // change is visible rather than silent.
    builderLegacyOverrideConflicts: (state) => findLegacyConflicts(
      state.builder.annotations,
      state.builder.procedures
    ),

    // ─── Settings getters (Slice 5) ───────────────────
    // Section value or default fallback. Used everywhere outside the Settings
    // editor (Builder right pane, preview header, etc.) so the UI doesn't
    // need to know whether the practice has saved yet.
    settingsGeneralResolved: (state) => {
      const stored = state.settings.sections.general;
      return stored ? { ...TX_PRESENTER_DEFAULTS.general, ...stored } : { ...TX_PRESENTER_DEFAULTS.general };
    },

    /**
     * MASTER gate for patient comments / questions on every OFFICE-side surface
     * (Builder Comments button + panel + badge polling, Settings nesting).
     * The patient page uses state.threadPatient.commentsEnabled instead — it is
     * public, has no customer.settings, and gets the resolved answer from the
     * server in the getTxPatientView payload.
     *
     * Two halves, matching tx-presenter-service/lib/comments-gate.js:
     *
     *  1. The stored flag must be a real JSON boolean `true`. Absent / null /
     *     'true' / 1 are OFF — the feature defaults OFF everywhere (2026-08-28),
     *     so `!== true` is the whole test and no absent-means-on backfill exists.
     *  2. The site must have a verified sender email. Comments with no sender is
     *     a dead end in both directions: the office is never told a question came
     *     in, and the patient is never told there is an answer.
     *
     * While customer.settings is still hydrating (siteChannelsReady false) this
     * returns TRUE — unknown is not denied. Same call as SendPlanModal's
     * siteEmailAvailable and the formsPaidFor/cherryEnabled getters above:
     * briefly showing a Comments button the server would 403 is a much smaller
     * failure than hiding the feature from a practice that paid for it on every
     * cold load. The server is the real gate either way.
     */
    commentsFeatureEnabled: (state, getters) => {
      if (getters.settingsGeneralResolved.commentsEnabled !== true) return false;
      if (!getters.siteChannelsReady) return true; // unknown ≠ denied (SendPlanModal precedent); the server is the gate
      return !!(getters.siteChannels && getters.siteChannels.email.available);
    },
    settingsPaymentResolved: (state) => {
      const stored = state.settings.sections.payment;
      if (!stored) return JSON.parse(JSON.stringify(TX_PRESENTER_DEFAULTS.payment));
      return {
        ...TX_PRESENTER_DEFAULTS.payment,
        ...stored,
        // Deep-merge options array — practice options replace defaults wholesale if present
        options: Array.isArray(stored.options) ? stored.options : TX_PRESENTER_DEFAULTS.payment.options,
      };
    },
    settingsHasGeneralSaved: (state) => state.settings.sections.general !== null,
    settingsHasPaymentSaved: (state) => state.settings.sections.payment !== null,
    // Practice "house style" — view enablement, default layout, and per-patient
    // display defaults (pricing layers, grouping, diagnostic visibility).
    // Soft migration: `enabledViews`/`defaultView` used to live in the general
    // blob; until a practice saves the Presentation section, inherit those two
    // from general so existing offices keep their saved views.
    settingsPresentationResolved: (state) => {
      const def = JSON.parse(JSON.stringify(TX_PRESENTER_DEFAULTS.presentation));
      const general = state.settings.sections.general;
      const stored = state.settings.sections.presentation;
      const out = def;
      // 1) Soft-migration fallback from general (only if presentation not saved yet).
      if (general) {
        if (general.enabledViews) out.enabledViews = { ...out.enabledViews, ...general.enabledViews };
        if (general.defaultView) out.defaultView = general.defaultView;
      }
      // 2) Saved presentation blob wins.
      if (stored) {
        if (stored.enabledViews) out.enabledViews = { ...out.enabledViews, ...stored.enabledViews };
        if (stored.defaultView) out.defaultView = stored.defaultView;
        if (stored.displayDefaults) {
          out.displayDefaults = {
            ...out.displayDefaults,
            ...stored.displayDefaults,
            layers: { ...out.displayDefaults.layers, ...(stored.displayDefaults.layers || {}) },
            viewVisibility: { ...out.displayDefaults.viewVisibility, ...(stored.displayDefaults.viewVisibility || {}) },
          };
        }
      }
      // 3) Disclaimer + acknowledgement lines (practice-wide, NOT a display default).
      //    ⛔ This getter - and SettingsPresentation.vue's watcher - rebuild the blob from
      //    whitelisted keys, so a key not named here is dropped on read and ERASED on the
      //    next Save. Run it through the shared normalizer so a hand-edited or partial
      //    blob can never reach the card or a patient in a surprising shape.
      out.disclaimer = normalizeDisclaimer(stored && stored.disclaimer);
      if (out.defaultView === 'phase') out.defaultView = 'region'; // coerce legacy
      return out;
    },
    settingsHasPresentationSaved: (state) => state.settings.sections.presentation !== null,
    settingsDeliveryResolved: (state) => {
      const stored = state.settings.sections.delivery;
      const defaults = TX_PRESENTER_DEFAULTS.delivery || {};
      return stored ? { ...defaults, ...stored } : { ...defaults };
    },
    settingsHasDeliverySaved: (state) => state.settings.sections.delivery !== null,
    // Slice 11 Phase A — code → form mappings for bundling consent forms
    // with sent plans. `mappings` is an array; we keep practice's saved
    // mappings (or default empty) without merging anything else into the
    // schema, since `consent` is a single-key object.
    settingsConsentResolved: (state) => {
      const stored = state.settings.sections.consent;
      const defaults = TX_PRESENTER_DEFAULTS.consent || { mappings: [] };
      if (!stored) return JSON.parse(JSON.stringify(defaults));
      return {
        mappings: Array.isArray(stored.mappings) ? stored.mappings : [],
      };
    },
    settingsHasConsentSaved: (state) => state.settings.sections.consent !== null,

    // ─── Patient view resolvers (Slice 7b) ───────────
    // Same priority chain as the Builder, sourcing data from the public payload
    // rather than the admin store. Returned as functions so the 3 view
    // components can use the same `labelFn` / `reasonFn` props.
    patientResolvedLabel: (state) => (proc) => {
      if (!proc) return '';
      const ov = state.patientView.overrides.labels[proc.procNum];
      if (ov) return ov;
      const ai = state.patientView.procedureContent.labels[proc.codeNum];
      if (ai) return ai;
      return proc.abbrDesc || proc.descript || proc.procCode || '—';
    },
    patientResolvedReason: (state) => (proc) => {
      if (!proc) return '';
      const ov = state.patientView.overrides.reasons[proc.procNum];
      if (ov) return ov;
      const ai = state.patientView.procedureContent.reasons[proc.codeNum];
      if (ai) return ai;
      return '';
    },
  },

  mutations: {
    // ─── Dashboard ─────────────────────────────────────
    SET_DASHBOARD_LOADING(state, val) { state.dashboard.loading = val; },
    SET_DASHBOARD_ERROR(state, val) { state.dashboard.error = val; },
    SET_DASHBOARD_DATA(state, payload) {
      state.dashboard.patients = payload.patients || [];
      state.dashboard.totalCount = payload.totalCount || 0;
      state.dashboard.fromDate = payload.fromDate || null;
      state.dashboard.toDate = payload.toDate || null;
      state.dashboard.siteID = payload.siteID || null;
      state.dashboard.lastFetchedAt = Date.now();
    },

    // ─── Filters ───────────────────────────────────────
    SET_FILTER(state, { key, value }) {
      Vue.set(state.filters, key, value);
    },

    // ─── Drawer ────────────────────────────────────────
    /**
     * `patNum` (optional): when opening for a DIFFERENT patient than the one
     * currently loaded, drop the previous payload so a failed or slow fetch can't
     * render its error banner over the last patient's data — TxPresenterDrawer's
     * body is `v-if="patient"`, so stale content stays visible otherwise.
     *
     * Same-patient re-opens must NOT clear: selectPlan re-opens this drawer to
     * switch plans and is meant to keep content on screen while the new plan loads.
     * CLOSE_DRAWER already clears everything, so open→open is the only leak.
     */
    OPEN_DRAWER(state, patNum) {
      state.drawer.open = true;
      const current = state.drawer.patient && state.drawer.patient.patNum;
      if (patNum != null && current != null && String(current) !== String(patNum)) {
        state.drawer.patient = null;
        state.drawer.plans = [];
        state.drawer.selectedTreatPlanNum = null;
        state.drawer.selectedPlan = null;
        state.drawer.internalNote = null;
        state.drawer.timeline = [];
      }
    },
    CLOSE_DRAWER(state) {
      state.drawer.open = false;
      state.drawer.patient = null;
      state.drawer.plans = [];
      state.drawer.selectedTreatPlanNum = null;
      state.drawer.selectedPlan = null;
      state.drawer.internalNote = null;
      state.drawer.timeline = [];
      state.drawer.siteID = null;
      state.drawer.error = null;
    },
    SET_DRAWER_LOADING(state, val) { state.drawer.loading = val; },
    SET_DRAWER_SAVING(state, val) { state.drawer.saving = val; },
    SET_DRAWER_ERROR(state, val) { state.drawer.error = val; },
    SET_DRAWER_DATA(state, payload) {
      state.drawer.patient = payload.patient || null;
      state.drawer.plans = payload.plans || [];
      state.drawer.selectedTreatPlanNum = payload.selectedTreatPlanNum || null;
      state.drawer.selectedPlan = payload.selectedPlan || null;
      state.drawer.internalNote = payload.internalNote || null;
      state.drawer.timeline = payload.timeline || [];
      state.drawer.siteID = payload.siteID || null;
    },
    SET_DRAWER_INTERNAL_NOTE(state, note) {
      state.drawer.internalNote = note;
    },

    // ─── Builder ───────────────────────────────────────
    SET_BUILDER_LOADING(state, val) { state.builder.loading = val; },
    SET_BUILDER_ERROR(state, val) { state.builder.error = val; },
    SET_BUILDER_SAVE_STATUS(state, val) { state.builder.saveStatus = val; },
    SET_BUILDER_CONTEXT(state, { siteID, patNum, treatPlanNum }) {
      state.builder.siteID = siteID || null;
      state.builder.patNum = patNum || null;
      state.builder.treatPlanNum = treatPlanNum || null;
    },
    SET_BUILDER_DATA(state, payload) {
      state.builder.plan = payload.plan || null;
      state.builder.procedures = payload.procedures || [];
      state.builder.proceduresOrder = payload.proceduresOrder || null;
      state.builder.annotations = payload.annotations || {};
      // Rehydrate the hide-by-priority selection from the plan-level annotation.
      // THIS is the line that carries the TC's view into Present Mode and onto
      // the tablet: both call loadBuilder, which RESET_BUILDERs first, so before
      // this the set was always empty by the time either painted and every
      // hidden group reappeared. Also covers the two refresh paths that reuse
      // this mutation. Absent ⇒ [] ⇒ nothing hidden, the pre-2026-09-10 default.
      state.builder.hiddenPriorityDefNums = Array.isArray(
        (payload.annotations || {}).hiddenPriorities
      )
        ? payload.annotations.hiddenPriorities
          .map((d) => parseInt(d, 10))
          .filter((d) => Number.isFinite(d))
        : [];
      // Per-patient payment overrides — null means practice defaults
      // remain in force; an array means the TC has customized.
      if (payload.paymentOptionOverrides !== undefined) {
        state.builder.paymentOptionOverrides = payload.paymentOptionOverrides || null;
      }
      state.builder.totalFee = payload.totalFee || 0;
      state.builder.totalNetFee = payload.totalNetFee || 0;
      // Guarantor account balance / credit (BalTotal − InsEst). Signed number.
      state.builder.familyBalance = Number(payload.familyBalance) || 0;
      // "Pay toward balance" ledger (Slice 3): paid payments for this plan.
      state.builder.payments = Array.isArray(payload.payments) ? payload.payments : [];
      state.builder.paidTotal = payload.paidTotal || 0;
      state.builder.lastSyncAt = Date.now();
      // Slice 10a #8 — concurrent-edit baseline. Captured on load so the
      // next save can send this value as `expectedUpdatedAt`. saveAnnotations
      // re-syncs it from the save response. null = no presentations row
      // exists yet (first edit on this plan, no concurrency check needed).
      if (payload.annotationsUpdatedAt !== undefined) {
        state.builder.annotationsUpdatedAt = payload.annotationsUpdatedAt || null;
      }
      // Slice 10a #9 — latest signed-PDF row for the retry banner.
      // Refresh-from-server is the source of truth; null = plan never signed.
      if (payload.latestSignedPdf !== undefined) {
        state.builder.latestSignedPdf = payload.latestSignedPdf || null;
      }
    },
    SET_BUILDER_LATEST_SIGNED_PDF(state, pdf) {
      state.builder.latestSignedPdf = pdf || null;
    },
    SET_BUILDER_REFRESH_OUTCOME(state, outcome) {
      state.builder.refreshOutcome = outcome || null;
    },
    SET_BUILDER_NEW_PROC_NUMS(state, procNums) {
      state.builder.newProcNums = Array.isArray(procNums) ? procNums : [];
    },
    CLEAR_BUILDER_NEW_PROC_NUM(state, procNum) {
      const idx = state.builder.newProcNums.indexOf(procNum);
      if (idx !== -1) state.builder.newProcNums.splice(idx, 1);
    },
    SET_BUILDER_ANNOTATIONS_UPDATED_AT(state, ts) {
      state.builder.annotationsUpdatedAt = ts || null;
    },
    SET_BUILDER_CONFLICT(state, conflict) {
      state.builder.conflict = conflict || null;
    },
    CLEAR_BUILDER_CONFLICT(state) {
      state.builder.conflict = null;
    },
    SET_BUILDER_PRIORITIES(state, priorities) {
      state.builder.priorities = priorities || [];
    },
    SET_BUILDER_PROCEDURES_ORDER(state, order) {
      state.builder.proceduresOrder = order;
    },
    SET_BUILDER_ANNOTATIONS(state, annotations) {
      state.builder.annotations = annotations || {};
    },
    SET_BUILDER_PAYMENT_OPTION_OVERRIDES(state, overrides) {
      // null = revert to practice defaults
      state.builder.paymentOptionOverrides = Array.isArray(overrides) ? overrides : null;
    },
    SET_BUILDER_CHERRY_TERMS(state, terms) {
      // Anything that isn't a terms object is "no terms" - never a half-formed one.
      state.builder.cherryTerms = terms && typeof terms === 'object' ? terms : null;
    },
    SET_BUILDER_CHERRY_TERMS_LOADING(state, val) { state.builder.cherryTermsLoading = !!val; },
    SET_BUILDER_PROC_ANNOTATION(state, { procNum, patch }) {
      const next = { ...(state.builder.annotations || {}) };
      next[procNum] = { ...(next[procNum] || {}), ...patch };
      Vue.set(state.builder, 'annotations', next);
    },
    SET_BUILDER_HIDDEN_PRIORITY_DEFNUMS(state, defNums) {
      Vue.set(state.builder, 'hiddenPriorityDefNums', defNums || []);
    },
    SET_BUILDER_GROUP_BY(state, mode) {
      state.builder.groupBy = mode || 'priority';
    },
    // ─── Builder procedure content (Slice 6) ─────────
    SET_PROCEDURE_CONTENT_LOADING(state, { type, val }) {
      const key = type === 'label' ? 'loadingLabels' : 'loadingReasons';
      state.builder.procedureContent[key] = val;
    },
    SET_PROCEDURE_CONTENT_BULK_REGEN(state, { type, val }) {
      const key = type === 'label' ? 'labels' : 'reasons';
      Vue.set(state.builder.procedureContent.bulkRegenerating, key, val);
    },
    SET_PROCEDURE_CONTENT_ROW_REGEN(state, { codeNum, type, val }) {
      const key = `${codeNum}:${type}`;
      if (val) Vue.set(state.builder.procedureContent.regenerating, key, true);
      else Vue.delete(state.builder.procedureContent.regenerating, key);
    },
    SET_PROCEDURE_CONTENT_ERROR(state, val) {
      state.builder.procedureContent.error = val;
    },
    MERGE_PROCEDURE_CONTENT(state, { type, items }) {
      const target = type === 'label'
        ? state.builder.procedureContent.labels
        : state.builder.procedureContent.reasons;
      (items || []).forEach((it) => {
        Vue.set(target, it.codeNum, {
          content: it.content || '',
          source: it.source || 'fallback',
          cached: !!it.cached,
        });
      });
    },

    // ─── Patient Tx Plans dispatcher (Slice 11 Phase C) ─
    OPEN_PATIENT_TX_PLANS_DISPATCHER(state, siteID) {
      state.patientTxPlansDispatcher.open = true;
      state.patientTxPlansDispatcher.siteID = siteID || null;
      state.patientTxPlansDispatcher.error = null;
    },
    SET_PATIENT_TX_PLANS_DISPATCHER_LOADING(state, val) {
      state.patientTxPlansDispatcher.loading = val;
    },
    SET_PATIENT_TX_PLANS_DISPATCHER_ERROR(state, val) {
      state.patientTxPlansDispatcher.error = val;
    },
    SET_PATIENT_TX_PLANS_DISPATCHER_DATA(state, { patient, plans }) {
      state.patientTxPlansDispatcher.patient = patient || null;
      state.patientTxPlansDispatcher.plans = Array.isArray(plans) ? plans : [];
    },
    CLOSE_PATIENT_TX_PLANS_DISPATCHER(state) {
      state.patientTxPlansDispatcher.open = false;
      state.patientTxPlansDispatcher.error = null;
    },
    RESET_PATIENT_TX_PLANS_DISPATCHER(state) {
      state.patientTxPlansDispatcher.open = false;
      state.patientTxPlansDispatcher.siteID = null;
      state.patientTxPlansDispatcher.patient = null;
      state.patientTxPlansDispatcher.plans = [];
      state.patientTxPlansDispatcher.loading = false;
      state.patientTxPlansDispatcher.error = null;
    },

    // ─── Signed plans summary (Patient Profile) ───────
    SET_SIGNED_PLANS_LOADING(state, val) {
      state.signedPlansSummary.loading = val;
    },
    SET_SIGNED_PLANS(state, plans) {
      state.signedPlansSummary.plans = Array.isArray(plans) ? plans : [];
    },
    RESET_SIGNED_PLANS(state) {
      state.signedPlansSummary.loading = false;
      state.signedPlansSummary.plans = [];
    },

    // ─── Patient photo library (Photo Attachments) ────
    SET_PATIENT_PHOTOS_LOADING(state, val) {
      state.patientPhotos.loading = !!val;
    },
    SET_PATIENT_PHOTOS(state, { photos, loadedFor }) {
      state.patientPhotos.list = Array.isArray(photos) ? photos : [];
      state.patientPhotos.loadedFor = loadedFor || null;
      state.patientPhotos.error = null;
    },
    SET_PATIENT_PHOTOS_ERROR(state, val) {
      state.patientPhotos.error = val || null;
    },
    ADD_PATIENT_PHOTO(state, photo) {
      if (!photo) return;
      // Newest first, matching the server's ORDER BY.
      state.patientPhotos.list = [photo, ...state.patientPhotos.list];
    },
    SET_PATIENT_PHOTO_TEETH(state, { photoId, teeth }) {
      const idx = state.patientPhotos.list.findIndex((p) => p.photoId === photoId);
      if (idx === -1) return;
      // Replace the object rather than mutating .teeth in place — Vue 2 can't
      // see an array element being swapped inside an object it already tracks.
      const next = { ...state.patientPhotos.list[idx], teeth: Array.isArray(teeth) ? teeth : [] };
      state.patientPhotos.list.splice(idx, 1, next);
    },
    REMOVE_PATIENT_PHOTO(state, photoId) {
      state.patientPhotos.list = state.patientPhotos.list.filter((p) => p.photoId !== photoId);
    },
    RESET_PATIENT_PHOTOS(state) {
      state.patientPhotos.list = [];
      state.patientPhotos.loadedFor = null;
      state.patientPhotos.loading = false;
      state.patientPhotos.error = null;
    },
    SET_PATIENT_VIEW_PHOTOS(state, { photos, showPhotos }) {
      state.patientView.photos = Array.isArray(photos) ? photos : [];
      state.patientView.photosEnabled = !!showPhotos;
      state.patientView.photosLoading = false;
    },
    SET_PATIENT_VIEW_PHOTOS_LOADING(state, val) {
      state.patientView.photosLoading = !!val;
    },

    // ─── Send-plan modal (Slice 7a) ───────────────────
    OPEN_SEND_PLAN(state) { state.sendPlan.open = true; },
    CLOSE_SEND_PLAN(state) {
      state.sendPlan.open = false;
      state.sendPlan.error = null;
    },
    SET_SEND_PLAN_GENERATING(state, val) { state.sendPlan.generating = val; },
    SET_SEND_PLAN_ERROR(state, val) { state.sendPlan.error = val; },
    SET_SEND_PLAN_URL(state, { shortId, fullUrl, reused, bundledFormIDs }) {
      state.sendPlan.shortId = shortId || null;
      state.sendPlan.fullUrl = fullUrl || null;
      state.sendPlan.reused = !!reused;
      state.sendPlan.persistedBundledFormIDs = Array.isArray(bundledFormIDs) ? bundledFormIDs : [];
    },
    SET_SEND_PLAN_PERSISTED_BUNDLE(state, ids) {
      state.sendPlan.persistedBundledFormIDs = Array.isArray(ids) ? ids : [];
    },
    RESET_SEND_PLAN(state) {
      state.sendPlan.open = false;
      state.sendPlan.shortId = null;
      state.sendPlan.fullUrl = null;
      state.sendPlan.reused = false;
      state.sendPlan.generating = false;
      state.sendPlan.error = null;
      state.sendPlan.persistedBundledFormIDs = [];
    },

    // ─── Patient view (Slice 7b) ──────────────────────
    SET_PATIENT_VIEW_LOADING(state, val) { state.patientView.loading = val; },
    SET_PATIENT_VIEW_ERROR(state, val) { state.patientView.error = val; },
    SET_PATIENT_VIEW_DATA(state, { shortId, data }) {
      state.patientView.shortId = shortId;
      // Patient/plan identifiers the payment path needs (the generic Moolah
      // endpoint has no shortId concept). Was previously dropped here → the
      // patient-view "Pay now" sent empty siteID/patNum and 400'd.
      state.patientView.resolved = data.resolved || null;
      state.patientView.practice = data.practice || null;
      state.patientView.patient = data.patient || null;
      state.patientView.plan = data.plan || null;
      state.patientView.procedures = data.procedures || [];
      state.patientView.altsByPrimary = data.altsByPrimary || {};
      state.patientView.procedureContent = data.procedureContent || { labels: {}, reasons: {} };
      state.patientView.overrides = data.overrides || { labels: {}, reasons: {} };
      state.patientView.paymentOptions = data.paymentOptions || null;
      // Cherry's real plans, stored by the backend when the office priced the plan. Absent on an
      // older backend or a plan with no Cherry option => null => the card shows no figures.
      state.patientView.cherryTerms = data.cherryTerms && typeof data.cherryTerms === 'object'
        ? data.cherryTerms
        : null;
      // Insurance disclaimer + acknowledgement lines (server-resolved). Absent on an
      // older backend or an office with the feature off => null => the sign panel is
      // exactly what it was before this feature (built-in checkbox, no paragraph).
      state.patientView.disclaimer = data.disclaimer && typeof data.disclaimer === 'object'
        ? data.disclaimer
        : null;
      // Two-level pricing display — per-view visibility + layer breakdowns
      // the TC chose in the Builder. Null when the plan was authored before
      // this slice landed; TxPatientView supplies defaults in that case.
      state.patientView.pricingDisplay = data.pricingDisplay || null;
      // Which views the patient can switch between + the default layout, from
      // the practice's Settings → General. Null on older backends; the
      // patient view falls back to both views enabled.
      state.patientView.viewConfig = data.viewConfig || null;
      state.patientView.preferences = data.preferences || { selectedIds: [], otherNote: '', updatedAt: null };
      state.patientView.latestSignature = data.latestSignature || null;
      state.patientView.totalFee = data.totalFee || 0;
      // Guarantor account balance / credit (BalTotal − InsEst). Signed number.
      state.patientView.familyBalance = Number(data.familyBalance) || 0;
      // "Pay toward balance" ledger (Slice 2): paid payments + remaining.
      state.patientView.payments = Array.isArray(data.payments) ? data.payments : [];
      state.patientView.paidTotal = data.paidTotal || 0;
      state.patientView.remaining = data.remaining || 0;
      // Slice 11 Phase B — bundled consent forms (FormType='OTHER'). Empty
      // array when nothing was bundled; the callout in TxPatientView is
      // hidden in that case.
      state.patientView.consentForms = Array.isArray(data.consentForms) ? data.consentForms : [];
      // Per-site capability, resolved server-side — this page is public and has no
      // customer.settings of its own, so these CANNOT be derived client-side.
      // Default-deny on an older payload that doesn't carry them: showing a patient
      // a Pay button that 500s is worse than not offering payment at all.
      state.patientView.paymentsEnabled = data.paymentsEnabled === true;
      // Whether the office can reach this patient on any channel. Drives the
      // post-sign copy, which used to promise an emailed copy unconditionally.
      state.patientView.officeCanNotify = data.officeCanNotify === true;
      // Slice 8a — initial thread page comes embedded in the patient-view
      // payload. Hydrate the threadPatient bucket so the thread renders on
      // first paint without a second round-trip.
      const t = data.thread || { comments: [], latestCommentID: 0 };
      state.threadPatient.comments = Array.isArray(t.comments) ? t.comments : [];
      state.threadPatient.latestCommentID = t.latestCommentID || 0;
      state.threadPatient.loading = false;
      state.threadPatient.error = null;
      // MASTER comments gate, resolved server-side (stored commentsEnabled === true
      // AND a verified sender email). DEFAULT-DENY — deliberately the same
      // `=== true` treatment as paymentsEnabled / officeCanNotify two blocks up,
      // NOT the absent-means-true instinct used for aiCommentsEnabled below.
      // aiCommentsEnabled could safely assume ON because the worst case was an
      // extra disclosure banner; here an absent key means an OLD backend, and
      // guessing ON would render a compose box whose POST the new backend 403s.
      // The deploy order (backend before frontend) closes that window.
      state.threadPatient.commentsEnabled = t.commentsEnabled === true;
      // Slice 8b — resolved AI gate. Defaults to true if backend didn't send
      // it (e.g., older Lambda still cached) so we don't disable the disclosure
      // banner unintentionally.
      state.threadPatient.aiCommentsEnabled = (typeof t.aiCommentsEnabled === 'boolean') ? t.aiCommentsEnabled : true;
      // Slice 8d-1 — AI delivery mode. 'auto' shows the "Office is replying…"
      // placeholder; 'review' skips the placeholder because the AI draft is
      // held for office approval and the patient shouldn't see anything
      // until released.
      state.threadPatient.aiReplyMode = t.aiReplyMode === 'review' ? 'review' : 'auto';
    },
    SET_PATIENT_LATEST_SIGNATURE(state, sig) { state.patientView.latestSignature = sig || null; },
    SET_PATIENT_PREFERENCES(state, prefs) {
      state.patientView.preferences = prefs || { selectedIds: [], otherNote: '', updatedAt: null };
    },
    SET_PATIENT_VIEW_SAVING_PREFS(state, val) { state.patientView.savingPreferences = val; },

    // ─── Sign flow (Slice 7d) ─────────────────────────
    SET_SIGN_STAGE(state, stage) { state.sign.stage = stage; },
    SET_SIGN_SUBMITTING(state, val) { state.sign.submitting = val; },
    SET_SIGN_RESULT(state, result) { state.sign.result = result; },
    SET_SIGN_ERROR(state, val) { state.sign.error = val; },
    RESET_SIGN(state) {
      state.sign.submitting = false;
      state.sign.stage = 'idle';
      state.sign.result = null;
      state.sign.error = null;
    },

    // ─── Comments / Q&A thread (Slice 8) ──────────────
    // Two buckets: 'builder' (admin Comments overlay) and 'patient' (patient
    // view inline thread). Each mutation takes a `which` discriminator so the
    // same setters work for both surfaces.
    SET_THREAD_LOADING(state, { which, val }) { state[which === 'patient' ? 'threadPatient' : 'threadBuilder'].loading = val; },
    SET_THREAD_POLLING(state, { which, val }) { state[which === 'patient' ? 'threadPatient' : 'threadBuilder'].polling = val; },
    SET_THREAD_ERROR(state, { which, val }) { state[which === 'patient' ? 'threadPatient' : 'threadBuilder'].error = val; },
    SET_THREAD_DATA(state, { which, comments, latestCommentID }) {
      const slot = state[which === 'patient' ? 'threadPatient' : 'threadBuilder'];
      slot.comments = Array.isArray(comments) ? comments : [];
      slot.latestCommentID = latestCommentID || 0;
    },
    APPEND_THREAD_DATA(state, { which, comments, latestCommentID }) {
      const slot = state[which === 'patient' ? 'threadPatient' : 'threadBuilder'];
      const existingIds = new Set(slot.comments.map((c) => c.commentID));
      const newOnes = (comments || []).filter((c) => !existingIds.has(c.commentID));
      slot.comments = [...slot.comments, ...newOnes];
      if (latestCommentID && latestCommentID > slot.latestCommentID) {
        slot.latestCommentID = latestCommentID;
      }
    },
    /**
     * Slice 8d-1 — replace a single comment in-place by CommentID. Used after
     * releaseAiReply flips VisibleToPatientAt server-side; we apply the fresh
     * row so the held badge / Approve button disappear without re-fetching.
     */
    UPDATE_THREAD_COMMENT_INPLACE(state, { which, comment }) {
      if (!comment || !comment.commentID) return;
      const slot = state[which === 'patient' ? 'threadPatient' : 'threadBuilder'];
      slot.comments = slot.comments.map((c) =>
        c.commentID === comment.commentID ? { ...c, ...comment } : c);
    },
    SET_THREAD_BUILDER_TARGET(state, { siteID, patNum, treatPlanNum }) {
      // Reset the bucket if the Builder switched plans (don't show old plan's thread)
      if (state.threadBuilder.siteID !== siteID
          || state.threadBuilder.patNum !== patNum
          || state.threadBuilder.treatPlanNum !== treatPlanNum) {
        state.threadBuilder.comments = [];
        state.threadBuilder.latestCommentID = 0;
        state.threadBuilder.error = null;
      }
      state.threadBuilder.siteID = siteID || null;
      state.threadBuilder.patNum = patNum || null;
      state.threadBuilder.treatPlanNum = treatPlanNum || null;
    },
    SET_THREAD_PANEL_OPEN(state, val) { state.threadBuilder.panelOpen = !!val; },

    // ─── Slice 8c — TC compose mutations ──────────────
    SET_TC_COMPOSE_SENDING(state, val) { state.threadBuilder.composing.sending = !!val; },
    SET_TC_COMPOSE_ERROR(state, val) { state.threadBuilder.composing.error = val || null; },
    SET_TC_COMPOSE_DRAFT(state, val) { state.threadBuilder.composing.draft = val || ''; },
    SET_TC_COMPOSE_REPLACING(state, { aiCommentID, prefill }) {
      state.threadBuilder.composing.replacing = aiCommentID || null;
      if (typeof prefill === 'string') state.threadBuilder.composing.draft = prefill;
    },
    CLEAR_TC_COMPOSE_REPLACING(state) {
      state.threadBuilder.composing.replacing = null;
    },
    INSERT_OPTIMISTIC_TC_COMMENT(state, { tempId, body, tcEmployeeNum }) {
      state.threadBuilder.composing.pendingId = tempId;
      state.threadBuilder.comments = [
        ...state.threadBuilder.comments,
        {
          commentID: tempId,
          author: 'office',
          tcEmployeeNum: tcEmployeeNum || null,
          body,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          readByPatientAt: null,
          readByOfficeAt: new Date().toISOString(),
          _pending: true,
        },
      ];
    },
    REPLACE_OPTIMISTIC_TC_COMMENT(state, { tempId, comment, replacedCommentID }) {
      // Drop the pending placeholder, drop the AI row that got hard-deleted
      // server-side (if any), append the real TC row from the response.
      const filtered = state.threadBuilder.comments.filter((c) =>
        c.commentID !== tempId && c.commentID !== replacedCommentID);
      const additions = comment ? [comment] : [];
      state.threadBuilder.comments = [...filtered, ...additions].sort((a, b) => a.commentID - b.commentID);
      if (comment && comment.commentID > state.threadBuilder.latestCommentID) {
        state.threadBuilder.latestCommentID = comment.commentID;
      }
      state.threadBuilder.composing.pendingId = 0;
      state.threadBuilder.composing.draft = '';
      state.threadBuilder.composing.replacing = null;
    },
    REMOVE_OPTIMISTIC_TC_COMMENT(state, { tempId }) {
      state.threadBuilder.comments = state.threadBuilder.comments.filter((c) =>
        c.commentID !== tempId);
      state.threadBuilder.composing.pendingId = 0;
    },
    SET_APPROVING_COMMENT_ID(state, val) {
      state.threadBuilder.composing.approvingCommentID = val || null;
    },

    // ─── Slice 8b — patient compose mutations ─────────
    SET_COMPOSE_SENDING(state, val) { state.threadPatient.composing.sending = !!val; },
    SET_COMPOSE_ERROR(state, val) { state.threadPatient.composing.error = val || null; },
    INSERT_OPTIMISTIC_PATIENT_COMMENT(state, { tempId, body }) {
      // Append a patient row with a negative commentID so it can't collide
      // with real auto-increment IDs. Replaced on /post-tx-comment success.
      state.threadPatient.composing.pendingPatientId = tempId;
      state.threadPatient.comments = [
        ...state.threadPatient.comments,
        {
          commentID: tempId,
          author: 'patient',
          tcEmployeeNum: null,
          body,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          readByPatientAt: null,
          readByOfficeAt: null,
          _pending: true,
        },
      ];
    },
    INSERT_OPTIMISTIC_AI_PLACEHOLDER(state, { tempId }) {
      // "Office is replying…" placeholder — only used when AI gates are ON.
      state.threadPatient.composing.pendingAiId = tempId;
      state.threadPatient.comments = [
        ...state.threadPatient.comments,
        {
          commentID: tempId,
          author: 'ai',
          tcEmployeeNum: null,
          body: '',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          readByPatientAt: null,
          readByOfficeAt: null,
          _pending: true,
          _placeholderText: 'Office is replying…',
        },
      ];
    },
    REPLACE_OPTIMISTIC_COMMENTS(state, { tempPatientId, tempAiId, patientComment, aiComment }) {
      // Drop any pending rows; merge in the real ones (sorted by commentID).
      const filtered = state.threadPatient.comments.filter((c) =>
        c.commentID !== tempPatientId && c.commentID !== tempAiId);
      const additions = [];
      if (patientComment) additions.push(patientComment);
      if (aiComment) additions.push(aiComment);
      state.threadPatient.comments = [...filtered, ...additions].sort((a, b) => a.commentID - b.commentID);
      const lastReal = state.threadPatient.comments
        .filter((c) => c.commentID > 0)
        .reduce((max, c) => Math.max(max, c.commentID), state.threadPatient.latestCommentID);
      state.threadPatient.latestCommentID = lastReal || state.threadPatient.latestCommentID;
      state.threadPatient.composing.pendingPatientId = 0;
      state.threadPatient.composing.pendingAiId = 0;
    },
    REMOVE_OPTIMISTIC_COMMENTS(state, { tempPatientId, tempAiId }) {
      state.threadPatient.comments = state.threadPatient.comments.filter((c) =>
        c.commentID !== tempPatientId && c.commentID !== tempAiId);
      state.threadPatient.composing.pendingPatientId = 0;
      state.threadPatient.composing.pendingAiId = 0;
    },

    // ─── Settings (Slice 5) ────────────────────────────
    SET_SETTINGS_LOADING(state, val) { state.settings.loading = val; },
    SET_SETTINGS_ERROR(state, val) { state.settings.error = val; },
    SET_SETTINGS_SAVING_SECTION(state, val) { state.settings.savingSection = val; },
    SET_SETTINGS_DATA(state, { siteID, sections }) {
      state.settings.siteID = siteID || null;
      Object.keys(sections || {}).forEach((key) => {
        Vue.set(state.settings.sections, key, sections[key]);
      });
      state.settings.lastFetchedAt = Date.now();
    },
    SET_SETTINGS_SECTION(state, { section, value }) {
      Vue.set(state.settings.sections, section, value);
    },

    RESET_BUILDER(state) {
      state.builder.patNum = null;
      state.builder.treatPlanNum = null;
      state.builder.siteID = null;
      state.builder.plan = null;
      state.builder.procedures = [];
      state.builder.proceduresOrder = null;
      state.builder.annotations = {};
      state.builder.paymentOptionOverrides = null;
      state.builder.cherryTerms = null;
      state.builder.cherryTermsLoading = false;
      state.builder.hiddenPriorityDefNums = [];
      state.builder.totalFee = 0;
      state.builder.totalNetFee = 0;
      state.builder.lastSyncAt = null;
      state.builder.refreshOutcome = null;
      state.builder.newProcNums = [];
      state.builder.saveStatus = 'idle';
      state.builder.error = null;
    },
  },

  actions: {
    /**
     * Load the dashboard data table for a site, applying current filters.
     * Default filters set in state; can be overridden in payload.
     */
    async fetchDashboard({ commit, state }, { siteID, ...overrides } = {}) {
      if (!siteID) {
        console.warn('[txPresenter] fetchDashboard called without siteID');
        return;
      }

      const seq = ++dashboardFetchSeq;
      commit('SET_DASHBOARD_LOADING', true);
      commit('SET_DASHBOARD_ERROR', null);

      try {
        const body = {
          siteID,
          windowDays: overrides.windowDays !== undefined ? overrides.windowDays : state.filters.windowDays,
          country: overrides.country || state.filters.country,
          providerNums: overrides.providerNums !== undefined ? overrides.providerNums : state.filters.providerNums,
          search: overrides.search !== undefined ? overrides.search : state.filters.search,
          limit: overrides.limit || state.filters.limit,
        };

        // Drop empty search to bypass the search-no-window-filter behavior cleanly
        if (!body.search) delete body.search;

        const response = await axios.post('/get-tx-presenter-dashboard', body, { crossdomain: true });

        // Latest-wins: a newer fetch superseded this one while it was in flight —
        // don't clobber its results (or its loading state, handled in finally).
        if (seq !== dashboardFetchSeq) return;

        if (response.data && response.data.success) {
          commit('SET_DASHBOARD_DATA', response.data.data);
          return response.data.data;
        }
        throw new Error(response.data?.message || 'Failed to load Tx Presenter dashboard');
      } catch (err) {
        console.error('[txPresenter] fetchDashboard error:', err);
        if (seq === dashboardFetchSeq) commit('SET_DASHBOARD_ERROR', err.message || 'Failed to load dashboard');
        throw err;
      } finally {
        // Only the latest fetch clears the spinner; a stale one finishing first
        // must not hide the loading state while the newer fetch is still running.
        if (seq === dashboardFetchSeq) commit('SET_DASHBOARD_LOADING', false);
      }
    },

    /**
     * Load drawer detail for a patient. Optional treatPlanNum selects a specific
     * plan; otherwise defaults to Active (or most recent saved if no Active).
     */
    async fetchPresentationDetail({ commit, state }, { siteID, patNum, treatPlanNum }) {
      if (!siteID) throw new Error('siteID is required');
      if (!patNum) throw new Error('patNum is required');

      commit('OPEN_DRAWER', patNum);
      commit('SET_DRAWER_LOADING', true);
      commit('SET_DRAWER_ERROR', null);

      try {
        const body = {
          siteID,
          patNum,
          country: state.filters.country,
        };
        if (treatPlanNum) body.treatPlanNum = treatPlanNum;

        const response = await axios.post('/get-tx-presentation-detail', body, { crossdomain: true });

        if (response.data && response.data.success) {
          commit('SET_DRAWER_DATA', response.data.data);
          return response.data.data;
        }
        throw new Error(response.data?.message || 'Failed to load presentation detail');
      } catch (err) {
        console.error('[txPresenter] fetchPresentationDetail error:', err);
        // extractApiError, not err.message: with an HTTP response axios's message is
        // always "Request failed with status code NNN", so the backend's own wording
        // ("Patient not found") never reached the drawer banner. A TC at Tulip Tree
        // re-clicked five times against that boilerplate on 2026-08-04.
        commit('SET_DRAWER_ERROR', extractApiError(err, 'Failed to load this patient.'));
        throw err;
      } finally {
        commit('SET_DRAWER_LOADING', false);
      }
    },

    /**
     * Switch the drawer's selected plan (re-fetches detail for that plan).
     */
    async selectPlan({ dispatch, state }, { treatPlanNum }) {
      if (!state.drawer.patient) return;
      return dispatch('fetchPresentationDetail', {
        siteID: state.drawer.siteID,
        patNum: state.drawer.patient.patNum,
        treatPlanNum,
      });
    },

    /**
     * Save the plan-level internal note for the currently-selected drawer plan.
     */
    async saveInternalNote({ commit, state }, { internalNote, setBy }) {
      if (!state.drawer.patient || !state.drawer.selectedTreatPlanNum) {
        throw new Error('No patient/plan selected');
      }

      commit('SET_DRAWER_SAVING', true);
      commit('SET_DRAWER_ERROR', null);

      try {
        const response = await axios.post('/save-tx-internal-note', {
          siteID: state.drawer.siteID,
          patNum: state.drawer.patient.patNum,
          treatPlanNum: state.drawer.selectedTreatPlanNum,
          internalNote,
          setBy: setBy || null,
        }, { crossdomain: true });

        if (response.data && response.data.success) {
          commit('SET_DRAWER_INTERNAL_NOTE', response.data.data.internalNote);
          return response.data.data;
        }
        throw new Error(response.data?.message || 'Failed to save internal note');
      } catch (err) {
        console.error('[txPresenter] saveInternalNote error:', err);
        commit('SET_DRAWER_ERROR', err.message || 'Failed to save note');
        throw err;
      } finally {
        commit('SET_DRAWER_SAVING', false);
      }
    },

    /**
     * Close the drawer and clear its state.
     */
    closeDrawer({ commit }) {
      commit('CLOSE_DRAWER');
    },

    /**
     * Update a filter and refetch dashboard.
     */
    async updateFilter({ commit, dispatch, rootState }, { key, value }) {
      commit('SET_FILTER', { key, value });
      const siteID = rootState.customer && rootState.customer.siteID;
      if (siteID) {
        await dispatch('fetchDashboard', { siteID });
      }
    },

    // ─── Builder actions (Slice 2) ─────────────────────

    /**
     * Load Builder workspace data — procedures + persisted annotations + priorities.
     * Resets the builder slice on entry so navigating between plans is clean.
     */
    async loadBuilder({ commit, state }, { siteID, patNum, treatPlanNum }) {
      if (!siteID) throw new Error('siteID is required');
      if (!patNum) throw new Error('patNum is required');
      if (!treatPlanNum) throw new Error('treatPlanNum is required');

      commit('RESET_BUILDER');
      commit('RESET_SEND_PLAN'); // Different plan → drop any cached short URL
      commit('SET_BUILDER_CONTEXT', { siteID, patNum, treatPlanNum });
      commit('SET_BUILDER_LOADING', true);
      commit('SET_BUILDER_ERROR', null);

      try {
        const planBody = {
          siteID,
          patNum,
          treatPlanNum,
          country: state.filters.country,
        };

        const [planRes, prioritiesRes] = await Promise.all([
          axios.post('/get-treatment-plan-data', planBody, { crossdomain: true }),
          axios.post('/get-tx-presenter-priorities', { siteID }, { crossdomain: true }),
        ]);

        if (!planRes.data || !planRes.data.success) {
          throw new Error(planRes.data?.message || 'Failed to load treatment plan data');
        }
        if (!prioritiesRes.data || !prioritiesRes.data.success) {
          throw new Error(prioritiesRes.data?.message || 'Failed to load tx priorities');
        }

        commit('SET_BUILDER_DATA', planRes.data.data);
        commit('SET_BUILDER_PRIORITIES', prioritiesRes.data.data.priorities || []);

        return planRes.data.data;
      } catch (err) {
        console.error('[txPresenter] loadBuilder error:', err);
        commit('SET_BUILDER_ERROR', err.message || 'Failed to load Builder');
        throw err;
      } finally {
        commit('SET_BUILDER_LOADING', false);
      }
    },

    /**
     * Refresh procedures from OD without losing local annotations / order.
     * Calls the same endpoint as loadBuilder but doesn't reset the slice — the
     * server returns the persisted annotations again, which is fine.
     *
     * SERIALIZED THROUGH annotationsSaveChain. Without that, a refresh landing
     * while a saveAnnotations POST is still in flight commits the PRE-EDIT
     * server snapshot over local state and the TC watches their last drag or
     * label edit silently revert. This is a pure read — queueing it behind
     * pending writes is the whole fix; it never enqueues a save of its own.
     * Rare when the only trigger was a 28px icon in the side panel; much less
     * rare now that a top-bar button and tab-refocus both fire it.
     *
     * Also diffs the procedure list so the caller can tell the TC what landed.
     * The diff lives HERE and not in SET_BUILDER_DATA because that mutation
     * also runs on loadBuilder, where every procedure would read as "new".
     */
    refreshBuilderFromOd({ commit, state }) {
      const exec = async () => {
      const { siteID, patNum, treatPlanNum } = state.builder;
      if (!siteID || !patNum || !treatPlanNum) {
        throw new Error('Builder context missing — call loadBuilder first');
      }

      commit('SET_BUILDER_LOADING', true);
      commit('SET_BUILDER_ERROR', null);

      // Snapshot before the commit, so the diff is against what the TC is
      // actually looking at right now.
      const beforeProcNums = (state.builder.procedures || []).map((p) => p.procNum);

      try {
        const response = await axios.post('/get-treatment-plan-data', {
          siteID,
          patNum,
          treatPlanNum,
          country: state.filters.country,
        }, { crossdomain: true });

        if (response.data && response.data.success) {
          const data = response.data.data;
          commit('SET_BUILDER_DATA', data);

          const diff = diffProcedureSets(beforeProcNums, data.procedures);
          commit('SET_BUILDER_REFRESH_OUTCOME', { ...diff, at: Date.now() });
          commit('SET_BUILDER_NEW_PROC_NUMS', mergeNewProcNums(
            state.builder.newProcNums,
            diff.added,
            data.procedures,
          ));

          return data;
        }
        throw new Error(response.data?.message || 'Failed to refresh from OD');
      } catch (err) {
        console.error('[txPresenter] refreshBuilderFromOd error:', err);
        // A failed refresh has no outcome to report — leave any previous
        // result and New badges alone rather than clearing them, so a dropped
        // connection doesn't erase what the last successful refresh surfaced.
        // Slice 10a #6 — when the plan no longer exists (e.g., deleted in OD
        // between sessions), clear the stale procedures so the Builder shows
        // an empty state instead of pretending the deleted plan still has
        // content. Only do this on plan-gone errors — a transient network
        // failure should NOT wipe what the TC was working on, because the
        // next retry will re-populate normally.
        const status = err.response && err.response.status;
        const msg = (err.message || '').toLowerCase();
        const planGone = status === 404 || msg.includes('not found') || msg.includes('treatment plan not found');
        if (planGone) {
          commit('SET_BUILDER_DATA', {
            plan: null,
            procedures: [],
            proceduresOrder: state.builder.proceduresOrder,
            annotations: state.builder.annotations,
          });
          commit('SET_BUILDER_NEW_PROC_NUMS', []);
          commit('SET_BUILDER_ERROR', 'This plan was not found in Open Dental — it may have been deleted. Return to the dashboard to pick another plan.');
        } else {
          commit('SET_BUILDER_ERROR', err.message || 'Failed to refresh');
        }
        throw err;
      } finally {
        commit('SET_BUILDER_LOADING', false);
      }
      }; // end exec

      // Same always-resolved chain discipline as saveAnnotations: a genuine
      // failure must not wedge later saves or leave an unhandled rejection,
      // while the returned promise still rejects so the caller can react.
      const result = annotationsSaveChain.then(exec, exec);
      annotationsSaveChain = result.catch(() => {});
      return result;
    },

    /**
     * Dismiss the "New" badge on one procedure once the TC has looked at it.
     */
    acknowledgeNewProcedure({ commit }, procNum) {
      commit('CLEAR_BUILDER_NEW_PROC_NUM', procNum);
    },

    /**
     * Persist proceduresOrder and/or annotations. Sets saveStatus to 'saving'
     * → 'saved' (or 'error') so the top bar can render a Google-Docs–style
     * indicator. Either field can be omitted; only provided fields update.
     */
    saveAnnotations({ commit, state, rootState }, { proceduresOrder, annotations, paymentOptionOverrides } = {}) {
      // Serialize through the module-level chain so concurrent dispatches run
      // one-at-a-time; each reads the freshly-committed annotationsUpdatedAt
      // baseline (no self-race 409). `exec` holds the actual save work.
      const exec = async () => {
      const { siteID, patNum, treatPlanNum } = state.builder;
      if (!siteID || !patNum || !treatPlanNum) {
        throw new Error('Builder context missing');
      }
      if (proceduresOrder === undefined && annotations === undefined && paymentOptionOverrides === undefined) return;

      commit('SET_BUILDER_SAVE_STATUS', 'saving');
      commit('SET_BUILDER_ERROR', null);

      try {
        const body = { siteID, patNum, treatPlanNum };
        if (proceduresOrder !== undefined) body.proceduresOrder = proceduresOrder;
        if (annotations !== undefined) body.annotations = annotations;
        if (paymentOptionOverrides !== undefined) body.paymentOptionOverrides = paymentOptionOverrides;
        const setBy = rootState.customer && rootState.customer.employeeID;
        if (setBy) body.setBy = setBy;
        // Slice 10a #8 — concurrent-edit guard. Backend compares this to
        // the persisted UpdatedAt; mismatch returns 409 with the other
        // TC's persisted state in the body.
        if (state.builder.annotationsUpdatedAt) {
          body.expectedUpdatedAt = state.builder.annotationsUpdatedAt;
        }

        const response = await axios.post('/save-tx-annotations', body, { crossdomain: true });

        if (response.data && response.data.success) {
          if (response.data.data.proceduresOrder !== undefined) {
            commit('SET_BUILDER_PROCEDURES_ORDER', response.data.data.proceduresOrder);
          }
          if (response.data.data.annotations !== undefined) {
            commit('SET_BUILDER_ANNOTATIONS', response.data.data.annotations);
          }
          if (response.data.data.paymentOptionOverrides !== undefined) {
            commit('SET_BUILDER_PAYMENT_OPTION_OVERRIDES', response.data.data.paymentOptionOverrides);
          }
          // Re-sync the baseline so the next save sends the fresh stamp.
          if (response.data.data.updatedAt) {
            commit('SET_BUILDER_ANNOTATIONS_UPDATED_AT', response.data.data.updatedAt);
          }
          commit('SET_BUILDER_SAVE_STATUS', 'saved');
          return response.data.data;
        }
        throw new Error(response.data?.message || 'Failed to save annotations');
      } catch (err) {
        // Slice 10a #8 — 409 CONCURRENT_EDIT: another TC edited the plan
        // between this caller's load and this save. Surface a conflict
        // banner with the other TC's persisted state so the UI can offer
        // a one-click "View their changes" path.
        const status = err.response && err.response.status;
        const payload = err.response && err.response.data;
        if (status === 409 && payload && payload.code === 'CONCURRENT_EDIT') {
          commit('SET_BUILDER_CONFLICT', {
            message: payload.message || 'This plan was edited by another team member.',
            persistedUpdatedAt: payload.data && payload.data.persistedUpdatedAt,
            proceduresOrder: payload.data && payload.data.proceduresOrder,
            annotations: payload.data && payload.data.annotations,
            paymentOptionOverrides: payload.data && payload.data.paymentOptionOverrides,
          });
          commit('SET_BUILDER_SAVE_STATUS', 'error');
          // Don't set BUILDER_ERROR — the conflict banner is its own UI
          // surface (more actionable than the generic top error banner).
          throw err;
        }
        console.error('[txPresenter] saveAnnotations error:', err);
        commit('SET_BUILDER_ERROR', err.message || 'Failed to save');
        commit('SET_BUILDER_SAVE_STATUS', 'error');
        throw err;
      }
      }; // end exec

      // Chain onto the queue; keep the chain itself always-resolved so a
      // genuine failure doesn't wedge subsequent saves or create an unhandled
      // rejection. The returned `result` still rejects so callers can react.
      const result = annotationsSaveChain.then(exec, exec);
      annotationsSaveChain = result.catch(() => {});
      return result;
    },

    /**
     * Slice 10a #8 — adopt the persisted state from a concurrent-edit
     * conflict and clear the banner. Equivalent to "refresh" but uses the
     * state already returned in the 409 response so we don't need another
     * round-trip. Future-friendly: when we add UpdatedBy attribution
     * to the row, the banner can show "Adopt <TC name>'s changes."
     */
    adoptConflictingState({ commit, state }) {
      const c = state.builder.conflict;
      if (!c) return;
      if (c.proceduresOrder !== undefined) {
        commit('SET_BUILDER_PROCEDURES_ORDER', c.proceduresOrder);
      }
      if (c.annotations !== undefined) {
        commit('SET_BUILDER_ANNOTATIONS', c.annotations);
      }
      if (c.paymentOptionOverrides !== undefined) {
        commit('SET_BUILDER_PAYMENT_OPTION_OVERRIDES', c.paymentOptionOverrides);
      }
      if (c.persistedUpdatedAt) {
        commit('SET_BUILDER_ANNOTATIONS_UPDATED_AT', c.persistedUpdatedAt);
      }
      commit('CLEAR_BUILDER_CONFLICT');
      commit('SET_BUILDER_SAVE_STATUS', 'idle');
    },

    dismissConflict({ commit }) {
      commit('CLEAR_BUILDER_CONFLICT');
    },

    /**
     * Slice 10a #9 — retry a signed-PDF row stuck in Status='failed'.
     * Resets row to 'processing' + re-dispatches the worker. The TC sees
     * the banner update from "failed" → "processing" optimistically, and
     * the row will move to "complete" via the worker (or back to "failed"
     * if the retry also fails — in which case the banner reappears).
     */
    async retrySignedPdf({ commit, state }) {
      const { siteID } = state.builder;
      const latest = state.builder.latestSignedPdf;
      if (!siteID || !latest || !latest.pdfId) return;
      if (latest.status !== 'failed') return;

      state.builder.retryingSignedPdf = true;
      try {
        const response = await axios.post('/retry-signed-pdf', {
          siteID, pdfId: latest.pdfId,
        }, { crossdomain: true });
        if (response.data && response.data.success) {
          const { status, dispatch } = response.data.data;
          commit('SET_BUILDER_LATEST_SIGNED_PDF', {
            ...latest,
            status,
            processingError: dispatch && dispatch.dispatched ? null : latest.processingError,
          });
          return response.data.data;
        }
        throw new Error(response.data?.message || 'Failed to retry signed PDF');
      } catch (err) {
        console.error('[txPresenter] retrySignedPdf error:', err);
        throw err;
      } finally {
        state.builder.retryingSignedPdf = false;
      }
    },

    /**
     * Drag-reorder result from the left pane. Optimistically writes the new
     * order to local state, then persists in background. The save action
     * confirms via SET_BUILDER_PROCEDURES_ORDER on success.
     */
    async reorderProcedures({ commit, dispatch }, { newOrder }) {
      commit('SET_BUILDER_PROCEDURES_ORDER', newOrder);
      return dispatch('saveAnnotations', { proceduresOrder: newOrder });
    },

    /**
     * Attach one or more procedures as alts of a primary. Per implementation
     * plan: alts must be from same patient (implicit — list is per-plan), can't
     * already be in another group (UI prevents but this also enforces). Mutates
     * the annotations blob and saves.
     *
     * Group-id convention: the primary's procNum is used as altGroupId. So:
     *   primary 86315 → annotations['86315'] = { altGroupId: '86315', isPrimaryInGroup: true,  altOrder: 0 }
     *   alt     86316 → annotations['86316'] = { altGroupId: '86315', isPrimaryInGroup: false, altOrder: 1 }
     *   alt     86317 → annotations['86317'] = { altGroupId: '86315', isPrimaryInGroup: false, altOrder: 2 }
     */
    async attachAlts({ commit, state, dispatch }, { primaryProcNum, altProcNums }) {
      if (!primaryProcNum || !Array.isArray(altProcNums) || altProcNums.length === 0) return;
      const annotations = { ...(state.builder.annotations || {}) };
      const primaryKey = String(primaryProcNum);
      const groupId = String(primaryProcNum);

      // Set/merge primary's annotation
      annotations[primaryKey] = {
        ...(annotations[primaryKey] || {}),
        altGroupId: groupId,
        isPrimaryInGroup: true,
        altOrder: 0,
      };

      // Determine next altOrder by counting existing alts in this group
      const existingOrders = Object.keys(annotations)
        .map((k) => annotations[k])
        .filter((a) => a && a.altGroupId === groupId && a.isPrimaryInGroup === false)
        .map((a) => a.altOrder || 0);
      let next = existingOrders.length > 0 ? Math.max(...existingOrders) + 1 : 1;

      altProcNums.forEach((pn) => {
        const key = String(pn);
        // Skip if already in any group (defensive — UI should prevent)
        const existing = annotations[key];
        if (existing && existing.altGroupId && existing.altGroupId !== groupId) return;
        // Skip if it's the primary itself
        if (parseInt(pn, 10) === parseInt(primaryProcNum, 10)) return;

        annotations[key] = {
          ...(existing || {}),
          altGroupId: groupId,
          isPrimaryInGroup: false,
          altOrder: next,
          // Ensure alts are not also hidden by a stale flag — the conflict rule
          // (explicit attach beats hide) is enforced by clearing this.
          isHiddenByPriority: false,
        };
        next++;
      });

      commit('SET_BUILDER_ANNOTATIONS', annotations);
      return dispatch('saveAnnotations', { annotations });
    },

    /**
     * Slice 9d — Present Mode alt swap. Promote an alt to be the new primary
     * of its group. Same group, same members, just re-keyed:
     *   - New primary's procNum becomes the group's altGroupId
     *   - Ex-primary auto-becomes an alt under the new primary (no data loss
     *     — patient can flip back via another tap)
     *   - Other alts in the group get their altGroupId updated to point at
     *     the new primary
     *
     * Returns the pre-swap annotations snapshot so the caller can wire an
     * undo action against it.
     */
    async swapAltToPrimary({ commit, state, dispatch }, { newPrimaryProcNum }) {
      if (!newPrimaryProcNum) return null;
      const annotations = { ...(state.builder.annotations || {}) };
      const newPrimaryKey = String(newPrimaryProcNum);
      const newPrimaryAnno = annotations[newPrimaryKey];

      // Safety: only swap rows that are currently alts in some group.
      if (!newPrimaryAnno || !newPrimaryAnno.altGroupId || newPrimaryAnno.isPrimaryInGroup) {
        return null;
      }

      // Capture pre-swap state for undo. Deep-clone so undo can't be
      // silently affected by later annotation edits in the same session.
      const previousAnnotations = JSON.parse(JSON.stringify(annotations));

      const oldGroupId = newPrimaryAnno.altGroupId;
      const newGroupId = newPrimaryKey;

      // Collect every member of the old group.
      const memberKeys = Object.keys(annotations).filter(
        (k) => annotations[k] && annotations[k].altGroupId === oldGroupId,
      );

      // Rewrite: tapped alt → primary; everyone else → alt under new primary.
      // altOrder is renumbered from 1 so the ex-primary slots in cleanly.
      let nextAltOrder = 1;
      memberKeys.forEach((k) => {
        if (k === newPrimaryKey) {
          annotations[k] = {
            ...annotations[k],
            altGroupId: newGroupId,
            isPrimaryInGroup: true,
            altOrder: 0,
          };
        } else {
          annotations[k] = {
            ...annotations[k],
            altGroupId: newGroupId,
            isPrimaryInGroup: false,
            altOrder: nextAltOrder++,
          };
        }
      });

      commit('SET_BUILDER_ANNOTATIONS', annotations);
      try {
        await dispatch('saveAnnotations', { annotations });
      } catch (e) {
        // Slice 10a #2 — if the save fails, the local commit above has
        // already moved the UI into the swapped state but the DB still
        // holds the pre-swap state. On next reload the swap would silently
        // vanish. Revert the local commit so the UI matches what's
        // actually persisted, then re-throw so the caller can surface
        // an error banner / toast to the TC.
        commit('SET_BUILDER_ANNOTATIONS', previousAnnotations);
        throw e;
      }
      return { previousAnnotations };
    },

    /**
     * Slice 9d — restore a pre-swap annotations snapshot. Used by the
     * "Undo" toast that pops after swapAltToPrimary. Just commits + saves
     * the snapshot; no special rewinding logic since the snapshot IS the
     * pre-state.
     */
    async undoAltSwap({ commit, dispatch }, { annotations }) {
      if (!annotations) return;
      commit('SET_BUILDER_ANNOTATIONS', annotations);
      return dispatch('saveAnnotations', { annotations });
    },

    /**
     * Detach a single alt — restores the proc to its OD-default position
     * (clearing the alt fields). If detaching the last alt of a group, the
     * primary's group fields are also cleared (so it's no longer a "primary"
     * with no alts).
     */
    async detachAlt({ commit, state, dispatch }, { procNum }) {
      const annotations = { ...(state.builder.annotations || {}) };
      const key = String(procNum);
      const target = annotations[key];
      if (!target || !target.altGroupId) return;

      const groupId = target.altGroupId;
      const wasPrimary = target.isPrimaryInGroup === true;

      // Remove alt fields from target
      const { altGroupId, isPrimaryInGroup, altOrder, ...rest } = target;
      if (Object.keys(rest).length > 0) {
        annotations[key] = rest;
      } else {
        delete annotations[key];
      }

      // Count remaining members in the group
      const remainingMembers = Object.keys(annotations).filter((k) => {
        const a = annotations[k];
        return a && a.altGroupId === groupId;
      });

      // If we detached an alt and only the primary remains, clear the primary's group fields too
      if (!wasPrimary && remainingMembers.length === 1) {
        const primaryKey = remainingMembers[0];
        const primary = annotations[primaryKey] || {};
        // eslint-disable-next-line no-unused-vars
        const { altGroupId: _g, isPrimaryInGroup: _p, altOrder: _o, ...primaryRest } = primary;
        if (Object.keys(primaryRest).length > 0) {
          annotations[primaryKey] = primaryRest;
        } else {
          delete annotations[primaryKey];
        }
      }

      // If we detached the primary itself, dissolve the whole group
      if (wasPrimary) {
        Object.keys(annotations).forEach((k) => {
          const a = annotations[k];
          if (a && a.altGroupId === groupId) {
            // eslint-disable-next-line no-unused-vars
            const { altGroupId: _g, isPrimaryInGroup: _p, altOrder: _o, ...altRest } = a;
            if (Object.keys(altRest).length > 0) {
              annotations[k] = altRest;
            } else {
              delete annotations[k];
            }
          }
        });
      }

      commit('SET_BUILDER_ANNOTATIONS', annotations);
      return dispatch('saveAnnotations', { annotations });
    },

    /**
     * Remove a single procedure from the presentation (Teamio-side only — the
     * proc is untouched in Open Dental). Stamps `annotations[procNum].removed =
     * { at, by }`, which drops it from every view derived from
     * builderVisibleProcedures and surfaces it in the "Removed from this plan"
     * strip. Restorable via restoreProcedure. Mirrors detachAlt's
     * clone-mutate-save idiom so there's exactly one persistence write.
     *
     * If the removed proc heads an alt group, its alts are returned to
     * standalone in the same write (their alt keys stripped, entry dropped if
     * empty). An attached alt isn't reachable from the remove UI, but if it
     * somehow is, only its own alt keys are stripped.
     */
    async removeProcedure({ commit, state, dispatch }, { procNum, removedBy }) {
      if (procNum === undefined || procNum === null) return;
      const annotations = { ...(state.builder.annotations || {}) };
      const key = String(procNum);
      const existing = annotations[key] || {};

      // Strip the target's own alt-group membership (whether primary or alt);
      // preserve every other key (overrides, etc.).
      // eslint-disable-next-line no-unused-vars
      const { altGroupId, isPrimaryInGroup, altOrder, ...targetRest } = existing;

      // Removed primary → dissolve its group, returning each alt to standalone.
      if (altGroupId && isPrimaryInGroup === true) {
        Object.keys(annotations).forEach((k) => {
          if (k === key) return;
          const a = annotations[k];
          if (a && a.altGroupId === altGroupId) {
            // eslint-disable-next-line no-unused-vars
            const { altGroupId: _g, isPrimaryInGroup: _p, altOrder: _o, ...rest } = a;
            if (Object.keys(rest).length > 0) annotations[k] = rest;
            else delete annotations[k];
          }
        });
      }

      annotations[key] = {
        ...targetRest,
        removed: { at: new Date().toISOString(), by: removedBy || null },
      };

      commit('SET_BUILDER_ANNOTATIONS', annotations);
      return dispatch('saveAnnotations', { annotations });
    },

    /**
     * Restore a procedure previously removed via removeProcedure — deletes the
     * `removed` key, dropping the annotation entry entirely if it becomes empty
     * (same cleanup as detachAlt).
     */
    async restoreProcedure({ commit, state, dispatch }, { procNum }) {
      const annotations = { ...(state.builder.annotations || {}) };
      const key = String(procNum);
      const target = annotations[key];
      if (!target || !target.removed) return;

      // eslint-disable-next-line no-unused-vars
      const { removed, ...rest } = target;
      if (Object.keys(rest).length > 0) annotations[key] = rest;
      else delete annotations[key];

      commit('SET_BUILDER_ANNOTATIONS', annotations);
      return dispatch('saveAnnotations', { annotations });
    },

    /**
     * Set the hide-by-priority multiselect and PERSIST it on the plan.
     *
     * It used to be session-local "per the design", and that design was wrong:
     * the TC built the view, hit Present on Tablet, and every hidden group came
     * back. Nothing was ignoring the selection — it was never saved, and it was
     * wiped twice on the way out (Builder beforeDestroy → closeBuilder, then
     * loadBuilder), while the tablet opened a whole different browser session
     * with nothing to carry over.
     *
     * Stored PLAN-level as `annotations.hiddenPriorities: [defNum, …]`, a
     * sibling of `pricingDisplay` — not as a per-procedure flag. A per-procedure
     * stamp is a snapshot: a procedure Open Dental adds to a hidden priority
     * afterwards would show up to the patient. The blob is free-form and
     * saveTxAnnotations is pass-through, so this needs no migration; absent ⇒
     * nothing hidden ⇒ exactly the old behaviour.
     */
    setHiddenPriorityDefNums({ commit, state, dispatch }, defNums) {
      const next = (defNums || [])
        .map((d) => parseInt(d, 10))
        .filter((d) => Number.isFinite(d));
      commit('SET_BUILDER_HIDDEN_PRIORITY_DEFNUMS', next);

      // No plan loaded (Present Mode bootstrap, tests) — keep it local rather
      // than POSTing an annotations blob against a null treatPlanNum.
      if (!state.builder.treatPlanNum || !state.builder.patNum) return Promise.resolve();

      const annotations = { ...(state.builder.annotations || {}) };
      if (next.length > 0) annotations.hiddenPriorities = next;
      else delete annotations.hiddenPriorities;
      commit('SET_BUILDER_ANNOTATIONS', annotations);
      return dispatch('saveAnnotations', { annotations });
    },

    /**
     * Set the local group-by mode. UI-only — does NOT persist.
     */
    setBuilderGroupBy({ commit }, mode) {
      commit('SET_BUILDER_GROUP_BY', mode);
    },

    /**
     * Reset the Builder slice — call when navigating away.
     */
    closeBuilder({ commit }) {
      commit('RESET_BUILDER');
    },

    // ─── Procedure content actions (Slice 6) ─────────

    /**
     * Load AI-generated content (labels or reasons) for the current Builder's
     * procedures. Default: only fetch missing CodeNums (those not yet in local
     * state). Cache-first on the backend, so this is cheap on repeat loads.
     */
    async loadProcedureContent({ commit, state, rootState }, { type, force = false } = {}) {
      if (type !== 'label' && type !== 'reason') {
        console.warn('[txPresenter] loadProcedureContent invalid type:', type);
        return;
      }
      const siteID = state.builder.siteID;
      if (!siteID) return;

      const target = type === 'label'
        ? state.builder.procedureContent.labels
        : state.builder.procedureContent.reasons;

      // Unique CodeNums for the current procedures
      const allCodeNums = [...new Set((state.builder.procedures || []).map((p) => p.codeNum).filter(Boolean))];
      const missing = force ? allCodeNums : allCodeNums.filter((c) => !target[c]);
      if (missing.length === 0) return;

      commit('SET_PROCEDURE_CONTENT_LOADING', { type, val: true });
      commit('SET_PROCEDURE_CONTENT_ERROR', null);

      try {
        const response = await axios.post('/generate-procedure-content', {
          siteID,
          codeNums: missing,
          type,
          force,
        }, await procedureContentConfig(rootState));
        if (response.data && response.data.success) {
          commit('MERGE_PROCEDURE_CONTENT', { type, items: response.data.data.items });
          return response.data.data;
        }
        throw new Error(response.data?.message || 'Failed to load procedure content');
      } catch (err) {
        console.error('[txPresenter] loadProcedureContent error:', err);
        commit('SET_PROCEDURE_CONTENT_ERROR', err.message || 'Failed to load AI content');
        // Don't throw — content load is non-blocking; views will fall back to abbrDesc
      } finally {
        commit('SET_PROCEDURE_CONTENT_LOADING', { type, val: false });
      }
    },

    /**
     * Force-regenerate a single CodeNum (e.g., user clicks regen on one row).
     * Bypasses cache; writes new value into cache and local state.
     */
    async regenerateProcedureContent({ commit, state, rootState }, { codeNum, type }) {
      const siteID = state.builder.siteID;
      if (!siteID || !codeNum || (type !== 'label' && type !== 'reason')) return;

      commit('SET_PROCEDURE_CONTENT_ROW_REGEN', { codeNum, type, val: true });
      try {
        const response = await axios.post('/generate-procedure-content', {
          siteID,
          codeNums: [codeNum],
          type,
          force: true,
        }, await procedureContentConfig(rootState));
        if (response.data && response.data.success) {
          commit('MERGE_PROCEDURE_CONTENT', { type, items: response.data.data.items });
          return response.data.data;
        }
        throw new Error(response.data?.message || 'Failed to regenerate');
      } catch (err) {
        console.error('[txPresenter] regenerateProcedureContent error:', err);
        commit('SET_PROCEDURE_CONTENT_ERROR', err.message || 'Failed to regenerate');
        throw err;
      } finally {
        commit('SET_PROCEDURE_CONTENT_ROW_REGEN', { codeNum, type, val: false });
      }
    },

    /**
     * Force-regenerate every CodeNum currently in the Builder. Used by the
     * "Regenerate all" button. Bypasses cache.
     */
    async regenerateAllProcedureContent({ commit, state, rootState }, { type }) {
      const siteID = state.builder.siteID;
      if (!siteID || (type !== 'label' && type !== 'reason')) return;

      const allCodeNums = [...new Set((state.builder.procedures || []).map((p) => p.codeNum).filter(Boolean))];
      if (allCodeNums.length === 0) return;

      commit('SET_PROCEDURE_CONTENT_BULK_REGEN', { type, val: true });
      commit('SET_PROCEDURE_CONTENT_ERROR', null);
      try {
        const response = await axios.post('/generate-procedure-content', {
          siteID,
          codeNums: allCodeNums,
          type,
          force: true,
        }, await procedureContentConfig(rootState));
        if (response.data && response.data.success) {
          commit('MERGE_PROCEDURE_CONTENT', { type, items: response.data.data.items });
          return response.data.data;
        }
        throw new Error(response.data?.message || 'Failed to regenerate all');
      } catch (err) {
        console.error('[txPresenter] regenerateAllProcedureContent error:', err);
        commit('SET_PROCEDURE_CONTENT_ERROR', err.message || 'Failed to regenerate');
        throw err;
      } finally {
        commit('SET_PROCEDURE_CONTENT_BULK_REGEN', { type, val: false });
      }
    },

    /**
     * Set or clear a per-plan override for a procedure CODE. `field` is
     * 'shortNameOverride' or 'reasonOverride'. Applies to every procedure on the
     * plan sharing that code. Pass empty string or null to clear.
     *
     * The transform also strips any legacy per-procNum values for the same code
     * — without that, clearing would resurrect the old per-tooth text through
     * the read-time shim. See utils/resolveProcContent.js.
     */
    async setCodeOverride({ commit, state, dispatch }, { codeNum, field, value }) {
      if (!codeNum || (field !== 'shortNameOverride' && field !== 'reasonOverride')) return;
      const annotations = applyCodeOverride(
        state.builder.annotations,
        state.builder.procedures,
        { codeNum, field, value }
      );
      commit('SET_BUILDER_ANNOTATIONS', annotations);
      return dispatch('saveAnnotations', { annotations });
    },

    // ─── Send-plan modal actions (Slice 7a) ───────────

    /**
     * Open the Send modal and (lazily) generate a short URL for the current
     * Builder plan. Re-uses an existing short URL if one is already mapped to
     * this (siteID, patNum, treatPlanNum) — so re-opening Send in the same
     * session doesn't create duplicate URLs.
     */
    async openSendPlan({ commit, state, rootState }) {
      const { siteID, patNum, treatPlanNum } = state.builder;
      if (!siteID || !patNum || !treatPlanNum) {
        commit('SET_SEND_PLAN_ERROR', 'No plan loaded — open the Builder first');
        commit('OPEN_SEND_PLAN');
        return;
      }
      commit('OPEN_SEND_PLAN');
      // If we already have a URL for this plan in local state and the plan
      // context hasn't changed, reuse it without a backend call.
      if (state.sendPlan.shortId && state.sendPlan.fullUrl) return;

      commit('SET_SEND_PLAN_GENERATING', true);
      commit('SET_SEND_PLAN_ERROR', null);
      try {
        const createdBy = (rootState.customer && rootState.customer.employeeID) || null;
        const response = await axios.post('/create-tx-short-url', {
          siteID, patNum, treatPlanNum, createdBy,
        }, { crossdomain: true });
        if (response.data && response.data.success) {
          commit('SET_SEND_PLAN_URL', response.data.data);
          return response.data.data;
        }
        throw new Error(response.data?.message || 'Failed to generate short URL');
      } catch (err) {
        console.error('[txPresenter] openSendPlan error:', err);
        commit('SET_SEND_PLAN_ERROR', err.message || 'Failed to generate link');
      } finally {
        commit('SET_SEND_PLAN_GENERATING', false);
      }
    },

    closeSendPlan({ commit }) { commit('CLOSE_SEND_PLAN'); },
    resetSendPlan({ commit }) { commit('RESET_SEND_PLAN'); },

    // ─── Patient Tx Plans dispatcher (Slice 11 Phase C) ─
    /**
     * Open the dispatcher modal from Patient Profile and load the patient's
     * saved plans. Reuses /get-tx-presentation-detail (Slice 1) which already
     * returns the patient + a list of plans. We don't pass treatPlanNum so the
     * backend doesn't waste effort fetching `selectedPlan` detail — the
     * dispatcher only needs the list.
     */
    async openPatientTxPlansDispatcher({ commit, state }, { siteID, patNum }) {
      if (!siteID) throw new Error('siteID is required');
      if (!patNum) throw new Error('patNum is required');

      commit('OPEN_PATIENT_TX_PLANS_DISPATCHER', siteID);

      // Drop the previous patient's payload when this is a DIFFERENT patient, so a
      // failed load can't show its error banner over stale plans (or fall through to
      // the "Plans are created in Open Dental" empty state, which reads as a fact
      // about the wrong patient). Done here rather than in the mutation on purpose:
      // selectPatientPlanForSend re-commits OPEN_… on error specifically to let the
      // TC pick again, and that path needs patient/plans to survive.
      const prev = state.patientTxPlansDispatcher.patient;
      if (prev && String(prev.patNum) !== String(patNum)) {
        commit('SET_PATIENT_TX_PLANS_DISPATCHER_DATA', { patient: null, plans: [] });
      }

      commit('SET_PATIENT_TX_PLANS_DISPATCHER_LOADING', true);
      commit('SET_PATIENT_TX_PLANS_DISPATCHER_ERROR', null);

      try {
        const response = await axios.post('/get-tx-presentation-detail', {
          siteID, patNum, country: state.filters.country,
        }, { crossdomain: true });
        if (response.data && response.data.success) {
          commit('SET_PATIENT_TX_PLANS_DISPATCHER_DATA', {
            patient: response.data.data.patient || null,
            plans: response.data.data.plans || [],
          });
          return response.data.data;
        }
        throw new Error(response.data?.message || 'Failed to load plans');
      } catch (err) {
        console.error('[txPresenter] openPatientTxPlansDispatcher error:', err);
        commit('SET_PATIENT_TX_PLANS_DISPATCHER_ERROR', extractApiError(err, 'Failed to load plans'));
      } finally {
        commit('SET_PATIENT_TX_PLANS_DISPATCHER_LOADING', false);
      }
    },

    closePatientTxPlansDispatcher({ commit }) {
      commit('CLOSE_PATIENT_TX_PLANS_DISPATCHER');
    },

    // ─── Signed plans summary (Patient Profile) ────────
    /**
     * Load the patient's Tx Presenter–signed plans + per-plan consent-form
     * status for the "Treatment Plans Signed" section on Patient Profile.
     * Non-fatal: on any error we log and commit an empty list so the section
     * simply shows its empty state instead of blocking the profile.
     */
    async fetchSignedPlansSummary({ commit }, { siteID, patNum }) {
      if (!siteID || !patNum) {
        console.warn('[txPresenter] fetchSignedPlansSummary called without siteID/patNum');
        commit('SET_SIGNED_PLANS', []);
        return;
      }
      commit('SET_SIGNED_PLANS_LOADING', true);
      try {
        const response = await axios.post('/get-tx-signed-plans-summary', {
          siteID, patNum,
        }, { crossdomain: true });
        commit('SET_SIGNED_PLANS', (response.data && response.data.plans) || []);
      } catch (err) {
        console.error('[txPresenter] fetchSignedPlansSummary error:', err);
        commit('SET_SIGNED_PLANS', []);
      } finally {
        commit('SET_SIGNED_PLANS_LOADING', false);
      }
    },

    /**
     * Resolve a short-lived presigned S3 URL for a signed-plan PDF. Returns the
     * url (no state) so the caller can window.open it; throws on failure so the
     * caller can surface a toast.
     */
    async fetchSignedPdfUrl(_ctx, { siteID, pdfId }) {
      if (!siteID) throw new Error('siteID is required');
      if (pdfId === null || pdfId === undefined) throw new Error('pdfId is required');
      const response = await axios.post('/get-tx-signed-pdf-url', {
        siteID, pdfId,
      }, { crossdomain: true });
      const url = response.data && response.data.url;
      if (!url) throw new Error('No URL returned for signed PDF');
      return url;
    },

    /**
     * Retry a signed-plan PDF render from the Patient Profile "Treatment Plans
     * Signed" section. Distinct from `retrySignedPdf` above, which is bound to
     * Builder state (no payload, gates on status==='failed', commits Builder
     * mutations) — this one is explicit-payload and also covers rows the backend
     * reports as STUCK (newest row 'processing' but past the staleness window),
     * which the Builder action deliberately refuses.
     *
     * Returns the response payload on accept. Throws on failure with the
     * server's message when present (409 = not retryable, 403 = cross-site,
     * 404 = missing) so the caller can surface it verbatim.
     */
    async retrySignedPlanPdf(_ctx, { siteID, pdfId }) {
      if (!siteID) throw new Error('siteID is required');
      if (pdfId === null || pdfId === undefined) throw new Error('pdfId is required');
      try {
        const response = await axios.post('/retry-signed-pdf', {
          siteID, pdfId,
        }, { crossdomain: true });
        if (response.data && response.data.success) return response.data.data;
        throw new Error(response.data?.message || 'Failed to retry signed PDF');
      } catch (err) {
        console.error('[txPresenter] retrySignedPlanPdf error:', err);
        // Prefer the server's explanation (e.g. 409 "still rendering") over the
        // generic axios "Request failed with status code 409".
        const serverMsg = err.response?.data?.message;
        if (serverMsg) throw new Error(serverMsg);
        throw err;
      }
    },

    /**
     * TC picks a plan from the dispatcher → load full Builder context + settings
     * (so SendPlanModal works exactly like it does from the Builder), then
     * close the dispatcher and open SendPlanModal.
     *
     * Calling loadBuilder is heavier than strictly necessary (annotations + AI
     * cache aren't needed for sending), but it keeps SendPlanModal's data
     * dependencies identical to the Builder path — no divergent code paths to
     * maintain. If perf becomes an issue we'd add a lightweight load action.
     */
    async selectPatientPlanForSend({ commit, dispatch, state }, { treatPlanNum }) {
      const { siteID, patient } = state.patientTxPlansDispatcher;
      if (!siteID) throw new Error('Dispatcher missing siteID — open dispatcher first');
      if (!patient || !patient.patNum) throw new Error('Dispatcher missing patient');
      if (!treatPlanNum) throw new Error('treatPlanNum is required');

      // Close the dispatcher first so the visual transition into SendPlanModal
      // is clean — one modal at a time.
      commit('CLOSE_PATIENT_TX_PLANS_DISPATCHER');

      try {
        await Promise.all([
          dispatch('loadBuilder', { siteID, patNum: patient.patNum, treatPlanNum }),
          dispatch('loadSettings', { siteID }),
        ]);
        await dispatch('openSendPlan');
      } catch (err) {
        console.error('[txPresenter] selectPatientPlanForSend error:', err);
        // Reopen the dispatcher so the user can see the error and pick again. This
        // relies on the mutation NOT clearing patient/plans — the pick-again list has
        // to survive. Same-patient clearing lives in openPatientTxPlansDispatcher.
        commit('OPEN_PATIENT_TX_PLANS_DISPATCHER', siteID);
        commit('SET_PATIENT_TX_PLANS_DISPATCHER_ERROR', extractApiError(err, 'Failed to open plan'));
      }
    },

    /**
     * Slice 11 Phase B — persist bundled consent forms independently of
     * /send-tx-link so the toggle in SendPlanModal sticks regardless of how
     * the TC delivers the link (SMS, email, or Copy Link). Called by a
     * watcher on the toggle; fire-and-forget from the caller's perspective
     * (errors are surfaced via console only — non-fatal for the modal flow).
     */
    async saveTxBundledForms({ state, commit }, { bundledFormIDs }) {
      const { siteID, patNum, treatPlanNum } = state.builder;
      if (!siteID || !patNum || !treatPlanNum) throw new Error('No plan loaded');
      if (!Array.isArray(bundledFormIDs)) throw new Error('bundledFormIDs must be an array');
      const response = await axios.post('/save-tx-bundled-forms', {
        siteID, patNum, treatPlanNum, bundledFormIDs,
      }, { crossdomain: true });
      if (response.data && response.data.success) {
        // Keep local state aligned so reopening the modal (which short-circuits
        // the createTxShortUrl refetch) reflects the latest saved bundle.
        commit('SET_SEND_PLAN_PERSISTED_BUNDLE', response.data.data.bundledFormIDs || []);
        return response.data.data;
      }
      throw new Error(response.data?.message || 'Failed to save bundled forms');
    },

    /**
     * Slice 7e — TC sends the plan link via SMS or email through the
     * SendPlanModal. Backend looks up patient.WirelessPhone or .Email,
     * gates on TxtMsgOk for SMS, and dispatches via per-site Twilio /
     * SendGrid. Returns { sent, channel, recipient, twilioMessageSid? }.
     *
     * Slice 11 Phase B — optional `bundledFormIDs: string[]` lets the TC
     * attach Teamio consent forms (FormType='OTHER') to this send. Backend
     * persists the list to tx_presenter_presentations.BundledConsentFormIDs
     * and the patient view surfaces them in an amber callout above the
     * procedures. Pass [] (or omit) to clear / skip.
     *
     * ⚠️ "Couldn't reach this patient" arrives as HTTP **200** with
     * `{ success:false, error, message }` — NOT as a 4xx. Lumigo pages on the
     * status a traced Lambda returns and treats 4xx like 5xx, so an ordinary
     * bad-number/opted-out outcome has to answer 2xx or it wakes someone up.
     * Read `error` for the machine code (OPTED_OUT / INVALID_NUMBER /
     * NO_PHONE / INVALID_EMAIL / NO_EMAIL / UNDELIVERABLE) and `message` for
     * the copy. Genuine faults still throw with a real 4xx/5xx.
     */
    async sendTxLink({ state, rootState }, { channel, recipient, customMessage, customSubject, customBody, bundledFormIDs, clientNonce }) {
      const { siteID, patNum, treatPlanNum } = state.builder;
      if (!siteID || !patNum || !treatPlanNum) throw new Error('No plan loaded');
      if (channel !== 'sms' && channel !== 'email') throw new Error("channel must be 'sms' or 'email'");

      const sentBy = (rootState.customer && rootState.customer.employeeID) || null;
      const body = { siteID, patNum, treatPlanNum, channel, sentBy };
      if (recipient) body.recipient = recipient;
      if (customMessage) body.customMessage = customMessage;
      if (customSubject) body.customSubject = customSubject;
      if (customBody) body.customBody = customBody;
      if (Array.isArray(bundledFormIDs)) body.bundledFormIDs = bundledFormIDs;
      // Per-attempt id (SMS only): part of the server's send-once key, so each deliberate click sends.
      if (typeof clientNonce === 'string' && clientNonce) body.clientNonce = clientNonce;

      let response;
      try {
        response = await axios.post('/send-tx-link', body, { crossdomain: true });
      } catch (err) {
        // A real transport/server failure. extractApiError is required here because
        // /opt/common serialises error bodies as a BARE JSON STRING, so the usual
        // `err.response.data.message` is always undefined and the message silently
        // degrades to axios's "Request failed with status code 500" — which is what a
        // TC at Tulip Tree actually saw, and re-clicked against, on 2026-08-04.
        throw new Error(extractApiError(err, 'Failed to send. Please try again.'));
      }
      if (response.data && response.data.success) return response.data.data;
      // Quiet-200 non-delivery. Carry the machine code on the Error so the modal can
      // treat "they opted out" differently from "that number looks wrong" without
      // string-matching the copy.
      const e = new Error(response.data?.message || 'Failed to send');
      e.code = response.data?.error || null;
      throw e;
    },

    /**
     * Set one top-level pricingDisplay flag on the loaded plan.
     *
     * Exists so a component that is NOT the Builder or Present Mode (today:
     * SendPlanModal, which mounts under three different parents) can flip a
     * display flag without a third hand-maintained copy of their
     * `savePricingDisplay` whitelists. Those whitelists are the reason "Total
     * only" once shipped broken — a fourth one is not the answer.
     *
     * Deliberately narrow: one known key, one boolean. Anything needing the
     * nested shapes (pricing, layers) should go through the parent's method.
     */
    setPricingDisplayFlag({ commit, state, dispatch }, { key, value } = {}) {
      const ALLOWED = ['showPhotos', 'showBalance', 'showDiagnostic'];
      if (ALLOWED.indexOf(key) === -1) {
        throw new Error(`setPricingDisplayFlag: unsupported key "${key}"`);
      }
      const annotations = { ...(state.builder.annotations || {}) };
      annotations.pricingDisplay = {
        ...(annotations.pricingDisplay || {}),
        [key]: !!value,
      };
      commit('SET_BUILDER_ANNOTATIONS', annotations);
      return dispatch('saveAnnotations', { annotations });
    },

    // ─── Patient photo library (Photo Attachments) ────

    /**
     * Load a patient's whole photo library (office side).
     *
     * Cached on `${siteID}:${patNum}`, so moving Builder → Present Mode or
     * switching between that patient's saved plans reuses what's already there.
     * Pass force:true after an upload/delete done outside these actions.
     */
    async loadPatientPhotos({ commit, state }, { siteID, patNum, force = false } = {}) {
      if (!siteID || !patNum) return [];
      const key = `${siteID}:${patNum}`;
      if (!force && state.patientPhotos.loadedFor === key) return state.patientPhotos.list;

      commit('SET_PATIENT_PHOTOS_LOADING', true);
      try {
        const response = await axios.post('/list-tx-photos', { siteID, patNum }, { crossdomain: true });
        const photos = (response.data && response.data.photos) || [];
        commit('SET_PATIENT_PHOTOS', { photos, loadedFor: key });
        return photos;
      } catch (err) {
        console.error('[txPresenter] loadPatientPhotos error:', err);
        commit('SET_PATIENT_PHOTOS_ERROR', extractApiError(err, 'Could not load photos.'));
        return [];
      } finally {
        commit('SET_PATIENT_PHOTOS_LOADING', false);
      }
    },

    /**
     * Upload one photo and record it against the patient.
     *
     * Two hops on purpose, matching how forms uploads work: the browser PUTs
     * (POSTs, really) straight to S3 with a presigned form, then tells us the
     * key. Nothing large passes through Lambda.
     *
     * uploadToS3 brings validation (image allowlist, 10MB) and client-side
     * compression along for free — see src/utils/fileUpload.js. The `endpoint`
     * override is why we don't need a new teamioType branch in that shared
     * helper.
     */
    async uploadPatientPhoto({ commit, state }, { siteID, patNum, file, teeth, setBy } = {}) {
      if (!siteID || !patNum) throw new Error('siteID and patNum are required');
      if (!file) throw new Error('file is required');

      if (state.patientPhotos.list.length >= MAX_PATIENT_PHOTOS) {
        throw new Error(
          `This patient already has ${MAX_PATIENT_PHOTOS} photos. Delete one before adding another.`
        );
      }

      const uploaded = await uploadToS3({
        file,
        siteID,
        patNum,
        teamioType: 'TX_PHOTO',
        endpoint: '/get-tx-photo-upload-url',
      });

      // uploadToS3 derives its return `key` from the presigned fields, which is
      // the same key the backend built — so this round-trips exactly and the
      // server's prefix check will pass.
      const response = await axios.post('/save-tx-photo', {
        siteID,
        patNum,
        key: uploaded.key,
        name: uploaded.name,
        type: uploaded.type,
        size: uploaded.size,
        teeth: Array.isArray(teeth) ? teeth : [],
        setBy: setBy || null,
      }, { crossdomain: true });

      const photo = response.data && response.data.photo;
      if (!photo) throw new Error('Photo uploaded but could not be saved. Please try again.');
      commit('ADD_PATIENT_PHOTO', photo);
      return photo;
    },

    /**
     * Retag which teeth a photo covers. Commits only on success — an optimistic
     * update here would flicker the camera icons in the plan views behind the
     * modal on any failure.
     */
    async updatePhotoTeeth({ commit }, { siteID, photoId, teeth } = {}) {
      if (!siteID || !photoId) throw new Error('siteID and photoId are required');
      const response = await axios.post('/update-tx-photo-teeth', {
        siteID, photoId, teeth: Array.isArray(teeth) ? teeth : [],
      }, { crossdomain: true });
      const saved = (response.data && response.data.teeth) || [];
      commit('SET_PATIENT_PHOTO_TEETH', { photoId, teeth: saved });
      return saved;
    },

    async deletePatientPhoto({ commit }, { siteID, photoId } = {}) {
      if (!siteID || !photoId) throw new Error('siteID and photoId are required');
      await axios.post('/delete-tx-photo', { siteID, photoId }, { crossdomain: true });
      commit('REMOVE_PATIENT_PHOTO', photoId);
    },

    /**
     * Patient-side photo fetch. Auth is the shortId and nothing else, so this
     * never sends siteID/patNum.
     *
     * Non-fatal by design: the plan must render even if photos fail. Also the
     * re-fetch path when a presigned URL has expired under a long-open tab —
     * PhotoLightbox re-dispatches this on an <img> error.
     */
    async loadPatientViewPhotos({ commit }, { shortId } = {}) {
      if (!shortId) return [];
      commit('SET_PATIENT_VIEW_PHOTOS_LOADING', true);
      try {
        const response = await axios.post('/get-tx-patient-photos', { shortId }, { crossdomain: true });
        const photos = (response.data && response.data.photos) || [];
        const showPhotos = !!(response.data && response.data.showPhotos);
        commit('SET_PATIENT_VIEW_PHOTOS', { photos, showPhotos });
        return photos;
      } catch (err) {
        console.error('[txPresenter] loadPatientViewPhotos error:', err);
        commit('SET_PATIENT_VIEW_PHOTOS', { photos: [], showPhotos: false });
        return [];
      }
    },

    // ─── Patient view actions (Slice 7b) ──────────────

    /**
     * Load the public patient-view payload via short URL token. Used by
     * TxPatientView.vue. No admin auth — only the shortId itself.
     */
    async loadPatientView({ commit }, { shortId }) {
      if (!shortId) throw new Error('shortId is required');

      commit('SET_PATIENT_VIEW_LOADING', true);
      commit('SET_PATIENT_VIEW_ERROR', null);

      try {
        const response = await axios.post('/get-tx-patient-view', { shortId }, { crossdomain: true });
        if (response.data && response.data.success) {
          commit('SET_PATIENT_VIEW_DATA', { shortId, data: response.data.data });
          return response.data.data;
        }
        throw new Error(response.data?.message || 'Failed to load plan');
      } catch (err) {
        console.error('[txPresenter] loadPatientView error:', err);
        const status = err.response && err.response.status;
        let msg = err.message || 'Failed to load plan';
        if (status === 404) msg = 'This link is no longer valid.';
        if (status === 410) msg = 'This link has expired. Please contact your dental office for a new one.';
        commit('SET_PATIENT_VIEW_ERROR', msg);
        throw err;
      } finally {
        commit('SET_PATIENT_VIEW_LOADING', false);
      }
    },

    /**
     * Save patient's payment preference toggles + optional 'Other' hybrid note.
     * Called on every toggle change (auto-save) and on textarea blur.
     * Optimistic local update — server is the source of truth on response.
     */
    async savePaymentPreference({ commit, state }, { selectedIds, otherNote }) {
      const shortId = state.patientView.shortId;
      if (!shortId) throw new Error('No active patient session');

      // Optimistic local update so the UI doesn't lag the toggle
      commit('SET_PATIENT_PREFERENCES', {
        selectedIds: Array.isArray(selectedIds) ? selectedIds : [],
        otherNote: otherNote || '',
        updatedAt: new Date().toISOString(),
      });

      commit('SET_PATIENT_VIEW_SAVING_PREFS', true);
      try {
        const response = await axios.post('/save-tx-payment-preference', {
          shortId,
          selectedIds: Array.isArray(selectedIds) ? selectedIds : [],
          otherNote: otherNote || '',
        }, { crossdomain: true });
        if (response.data && response.data.success) {
          commit('SET_PATIENT_PREFERENCES', response.data.data);
          return response.data.data;
        }
        throw new Error(response.data?.message || 'Failed to save preferences');
      } catch (err) {
        console.error('[txPresenter] savePaymentPreference error:', err);
        commit('SET_PATIENT_VIEW_ERROR', err.message || 'Failed to save your preferences');
        throw err;
      } finally {
        commit('SET_PATIENT_VIEW_SAVING_PREFS', false);
      }
    },

    // ─── Sign flow actions (Slice 7d) ─────────────────
    //
    // Phase 3 of the @react-pdf migration replaced the in-browser PDF render.
    // The browser used to load html2pdf.js, screenshot the patient-view paper,
    // upload the result to S3 via a presigned POST, and only then call
    // /sign-tx-presentation. That path was slow (3-8s blocking spinner) and
    // produced the dropped-pages / low-resolution output we replaced.
    //
    // Now: capture the signature canvas as a base64 PNG, POST it to
    // /sign-tx-presentation, return immediately. The backend renders the PDF
    // server-side via @react-pdf, fans out S3 + SQS + email asynchronously,
    // and the patient sees the success state in well under a second.

    /**
     * Submit the signing event. Sends signerName + signatureImage + a snapshot
     * of the plan/preferences the patient saw. Backend resolves shortId from
     * the active session, INSERTs the row, and dispatches the async worker.
     */
    async signPresentation({ state }, { shortId, signerName, signatureImage, planSnapshot, preferencesSnapshot, acknowledgedIds }) {
      // shortId is explicit for in-office signing (Present Mode), or falls back
      // to the active patient session (at-home patient view).
      const sid = shortId || state.patientView.shortId;
      if (!sid) throw new Error('No active session for signing');
      const response = await axios.post('/sign-tx-presentation', {
        shortId: sid, signerName, signatureImage, planSnapshot, preferencesSnapshot,
        // Ids of the office's acknowledgement lines the patient ticked. The SERVER
        // is the gate (400 when a required line is missing) and stamps the frozen
        // record from its own settings - this is only which boxes were ticked.
        acknowledgedIds: Array.isArray(acknowledgedIds) ? acknowledgedIds : [],
      }, { crossdomain: true });
      if (response.data && response.data.success) return response.data.data;
      throw new Error(response.data?.message || 'Failed to record signature');
    },

    /**
     * Ensure a short URL exists for a plan and return its shortId. Idempotent
     * — reuses an existing URL or creates one. No notification or status
     * side effects (that's sendTxLink). Used by Present Mode to obtain a
     * shortId for chairside signing without opening the Send modal.
     */
    async ensureTxShortUrl({ rootState }, { siteID, patNum, treatPlanNum }) {
      if (!siteID || !patNum || !treatPlanNum) throw new Error('Plan context required');
      const createdBy = (rootState.customer && rootState.customer.employeeID) || null;
      const response = await axios.post('/create-tx-short-url', {
        siteID, patNum, treatPlanNum, createdBy,
      }, { crossdomain: true });
      if (response.data && response.data.success && response.data.data) {
        return response.data.data.shortId;
      }
      throw new Error(response.data?.message || 'Failed to prepare signing session');
    },

    /**
     * Submit-sign orchestrator. Caller passes:
     *   - signerName: typed name from the form
     *   - signatureDataUrl: PNG data URL from the v-perfect-signature canvas
     *
     * Stages: submitting → done. Backend renders + emails asynchronously, so
     * the patient sees "done" almost immediately and the success copy explains
     * the email is on its way.
     */
    async submitSignFlow({ commit, dispatch, state }, { signerName, signatureDataUrl, acknowledgedIds, context }) {
      commit('RESET_SIGN');
      commit('SET_SIGN_SUBMITTING', true);
      commit('SET_SIGN_STAGE', 'submitting');

      try {
        // `context` is passed for in-office signing (Present Mode) — it carries
        // an explicit shortId + a snapshot built from the Builder state. Without
        // it, fall back to the at-home patient-session snapshot.
        const planSnapshot = context ? context.planSnapshot : {
          procedures: state.patientView.procedures,
          altsByPrimary: state.patientView.altsByPrimary,
          plan: state.patientView.plan,
          totalFee: state.patientView.totalFee,
          // Freeze the guarantor balance shown at signing. The showBalance
          // toggle is resolved live at render; this is the exact dollar amount
          // the patient saw folded into their Estimated Patient Portion.
          familyBalance: state.patientView.familyBalance,
        };
        const preferencesSnapshot = context
          ? context.preferencesSnapshot
          : state.patientView.preferences;

        const result = await dispatch('signPresentation', {
          shortId: context ? context.shortId : undefined,
          signerName,
          signatureImage: signatureDataUrl || null,
          planSnapshot,
          preferencesSnapshot,
          acknowledgedIds,
        });

        commit('SET_SIGN_RESULT', result);
        commit('SET_SIGN_STAGE', 'done');
        return result;
      } catch (err) {
        console.error('[txPresenter] submitSignFlow error:', err);
        // extractApiError, not err.message: with an HTTP response axios's message is
        // "Request failed with status code 400", which would hide the server's explicit
        // "Please check the required acknowledgements before signing." from the patient.
        commit('SET_SIGN_ERROR', extractApiError(err, 'Something went wrong while signing.'));
        commit('SET_SIGN_STAGE', 'error');
        throw err;
      } finally {
        commit('SET_SIGN_SUBMITTING', false);
      }
    },

    resetSign({ commit }) { commit('RESET_SIGN'); },

    // ─── Comments / Q&A thread actions (Slice 8) ──────
    //
    // Two parallel actions per surface — `loadThread*` resets the bucket and
    // pulls from since=0 (used on first open / context switch), `pollThread*`
    // sends `since=latestCommentID` and APPENDS only new rows (used by the
    // 10s focus-only polling loop). Both share the same backend endpoint
    // /get-tx-thread-since with different auth shapes.
    //
    // 8a is read-only — compose actions land in 8b (patient post + AI reply)
    // and 8c (TC reply / override).

    async loadThreadBuilder({ commit, getters }, { siteID, patNum, treatPlanNum }) {
      // Single choke point for the office read path. The Builder has three
      // dispatch sites (bootstrap badge, plan switch, panel open) and gating
      // each one is how a surface gets missed; guarding here covers all of them,
      // present and future. The target is recorded BEFORE the return so that
      // flipping the setting on can start polling without a re-dispatch.
      commit('SET_THREAD_BUILDER_TARGET', { siteID, patNum, treatPlanNum });
      if (!getters.commentsFeatureEnabled) return;
      commit('SET_THREAD_LOADING', { which: 'builder', val: true });
      commit('SET_THREAD_ERROR', { which: 'builder', val: null });
      try {
        const response = await axios.post('/get-tx-thread-since', {
          siteID, patNum, treatPlanNum, since: 0,
        });
        if (response.data && response.data.success) {
          commit('SET_THREAD_DATA', {
            which: 'builder',
            comments: response.data.data.comments,
            latestCommentID: response.data.data.latestCommentID,
          });
        } else {
          throw new Error(response.data?.message || 'Failed to load comments');
        }
      } catch (err) {
        console.error('[txPresenter] loadThreadBuilder error:', err);
        commit('SET_THREAD_ERROR', { which: 'builder', val: err.message || 'Failed to load comments' });
      } finally {
        commit('SET_THREAD_LOADING', { which: 'builder', val: false });
      }
    },

    async pollThreadBuilder({ commit, state, getters }) {
      // Master gate. A 10s interval can outlive the toggle being turned off
      // (the panel watcher stops it, but the interval is also restarted by the
      // visibilitychange handler), so re-check on every tick rather than trusting
      // the caller to have torn it down.
      if (!getters.commentsFeatureEnabled) return;
      const t = state.threadBuilder;
      if (!t.siteID || !t.patNum || !t.treatPlanNum) return;
      if (t.polling) return; // re-entrancy guard
      commit('SET_THREAD_POLLING', { which: 'builder', val: true });
      try {
        const response = await axios.post('/get-tx-thread-since', {
          siteID: t.siteID, patNum: t.patNum, treatPlanNum: t.treatPlanNum,
          since: t.latestCommentID || 0,
        });
        if (response.data && response.data.success) {
          commit('APPEND_THREAD_DATA', {
            which: 'builder',
            comments: response.data.data.comments,
            latestCommentID: response.data.data.latestCommentID,
          });
        }
      } catch (err) {
        // Polling errors are non-fatal — log and continue. We don't surface
        // a banner because the panel is showing existing thread already.
        console.warn('[txPresenter] pollThreadBuilder error (non-fatal):', err.message);
      } finally {
        commit('SET_THREAD_POLLING', { which: 'builder', val: false });
      }
    },

    async pollThreadPatient({ commit, state }) {
      // Master gate, patient side. Reads the server-resolved flag off state, NOT
      // the commentsFeatureEnabled getter: the patient page is public and has no
      // customer.settings, so that getter would resolve from an empty settings
      // blob. When comments are off the backend already answers with an empty
      // page, so this only saves a pointless request every 10 seconds.
      if (!state.threadPatient.commentsEnabled) return;
      const shortId = state.patientView.shortId;
      if (!shortId) return;
      const t = state.threadPatient;
      if (t.polling) return;
      commit('SET_THREAD_POLLING', { which: 'patient', val: true });
      try {
        const response = await axios.post('/get-tx-thread-since', {
          shortId,
          since: t.latestCommentID || 0,
        }, { crossdomain: true });
        if (response.data && response.data.success) {
          commit('APPEND_THREAD_DATA', {
            which: 'patient',
            comments: response.data.data.comments,
            latestCommentID: response.data.data.latestCommentID,
          });
        }
      } catch (err) {
        console.warn('[txPresenter] pollThreadPatient error (non-fatal):', err.message);
      } finally {
        commit('SET_THREAD_POLLING', { which: 'patient', val: false });
      }
    },

    openCommentsPanel({ commit, dispatch, state }) {
      commit('SET_THREAD_PANEL_OPEN', true);
      // Lazy load on open if we don't already have a fresh thread for this plan.
      const b = state.builder;
      if (b && b.siteID && b.patNum && b.treatPlanNum) {
        dispatch('loadThreadBuilder', {
          siteID: b.siteID,
          patNum: b.patNum,
          treatPlanNum: b.treatPlanNum,
        });
      }
    },
    closeCommentsPanel({ commit }) { commit('SET_THREAD_PANEL_OPEN', false); },

    // ─── Slice 8c — TC compose actions ────────────────

    /**
     * TC kicks off "Replace this reply" — pre-fills the compose textarea
     * with the AI's text so they can lightly edit instead of retyping.
     */
    enterReplaceMode({ commit, state }, { aiCommentID }) {
      const aiRow = state.threadBuilder.comments.find((c) => c.commentID === aiCommentID);
      const prefill = aiRow ? aiRow.body : '';
      commit('SET_TC_COMPOSE_REPLACING', { aiCommentID, prefill });
      commit('SET_TC_COMPOSE_ERROR', null);
    },
    cancelReplaceMode({ commit }) {
      commit('CLEAR_TC_COMPOSE_REPLACING');
      commit('SET_TC_COMPOSE_DRAFT', '');
      commit('SET_TC_COMPOSE_ERROR', null);
    },
    setTcDraft({ commit }, val) { commit('SET_TC_COMPOSE_DRAFT', val); },
    clearTcComposeError({ commit }) { commit('SET_TC_COMPOSE_ERROR', null); },

    /**
     * Slice 8d-1 — TC approves a held AI draft. Backend flips the row's
     * VisibleToPatientAt = NOW(); we update the local row in place so the
     * Builder thread loses its "Pending your review" badge and the Approve/
     * Edit buttons disappear.
     *
     * If something goes wrong we leave the row as held — the Approve button
     * stays visible so TC can retry.
     */
    async releaseAiReply({ commit, state }, { commentID }) {
      console.log('[releaseAiReply] entered, commentID:', commentID);
      const t = state.threadBuilder;
      if (!t.siteID || !t.patNum || !t.treatPlanNum) {
        throw new Error('No active plan in builder');
      }
      // Slice 8 UX — track which row is being released so CommentsThread can
      // show a spinner + disable the Approve/Edit buttons for that row.
      // Re-entrancy guard: if this row is already being approved, no-op.
      if (t.composing.approvingCommentID === commentID) {
        console.log('[releaseAiReply] re-entrancy guard hit, skipping');
        return;
      }
      const startedAt = Date.now();
      commit('SET_APPROVING_COMMENT_ID', commentID);
      console.log('[releaseAiReply] approvingCommentID set to:', state.threadBuilder.composing.approvingCommentID);
      let result;
      try {
        const response = await axios.post('/release-ai-reply', {
          siteID: t.siteID,
          patNum: t.patNum,
          treatPlanNum: t.treatPlanNum,
          commentID,
        });
        console.log('[releaseAiReply] response status:', response.status);
        if (!response.data || !response.data.success) {
          throw new Error(response.data && response.data.message ? response.data.message : 'Failed to approve reply');
        }
        result = response.data.data;
      } catch (err) {
        console.error('[releaseAiReply] error:', err.response ? err.response.status : 'no-status', err);
        // Ensure the spinner is visible for the minimum window before we
        // bail on error too — otherwise a fast 4xx flashes invisibly.
        const elapsed = Date.now() - startedAt;
        if (elapsed < 500) await new Promise((r) => setTimeout(r, 500 - elapsed));
        commit('SET_APPROVING_COMMENT_ID', null);
        throw err;
      }

      // Slice 8 UX — guarantee the "Sending…" spinner stays visible for at
      // least 500ms so the TC always sees "something happened." Without
      // this, a fast response (e.g., the 5-min notify batch gate is hit and
      // the backend returns in ~50ms) would flash the spinner invisibly and
      // the row would just suddenly lose its "Pending your review" badge —
      // unsettling for the TC who clicked.
      const elapsed = Date.now() - startedAt;
      if (elapsed < 500) await new Promise((r) => setTimeout(r, 500 - elapsed));

      // Apply the fresh row + clear the approving flag together so the
      // visual transition (badge gone, buttons gone) happens AFTER the
      // spinner-visible window.
      const fresh = result && result.comment;
      if (fresh) commit('UPDATE_THREAD_COMMENT_INPLACE', { which: 'builder', comment: fresh });
      commit('SET_APPROVING_COMMENT_ID', null);
      console.log('[releaseAiReply] approvingCommentID cleared');
      return result;
    },

    /**
     * TC posts a reply. If composing.replacing is set, asks the backend to
     * override that AI row — backend hard-deletes the AI row when patient
     * hasn't read it, leaves it alone otherwise (audit trail). Either way
     * the new TC row is inserted and returned in the response.
     */
    async postTcMessage({ commit, state, rootState }, payload) {
      console.log('[postTcMessage] entered');
      const { siteID: argSiteID, patNum: argPatNum, treatPlanNum: argTreatPlanNum, body } = payload || {};
      const siteID = argSiteID || state.threadBuilder.siteID;
      const patNum = argPatNum || state.threadBuilder.patNum;
      const treatPlanNum = argTreatPlanNum || state.threadBuilder.treatPlanNum;

      // Surface validation failures in the UI by committing the error BEFORE
      // returning — otherwise the throw bubbles up to onTcSend's catch and
      // disappears silently. (We don't enter the try/finally below, so
      // sending state is never flipped to true.)
      const surface = (msg) => {
        console.error('[postTcMessage]', msg);
        commit('SET_TC_COMPOSE_ERROR', msg);
      };

      if (!siteID || !patNum || !treatPlanNum) { surface('No active plan in builder'); return; }
      const trimmed = String(body || '').trim();
      if (!trimmed) { surface('Reply cannot be empty'); return; }

      // The logged-in user object is at rootState.customer.user (loaded from
      // localStorage on app boot). EmployeeID casing varies across the
      // codebase (chat.js handles both EmployeeID + employeeID + _id) so
      // mirror that fallback chain here.
      const u = (rootState.customer && rootState.customer.user) || {};
      const tcEmployeeNum = u.EmployeeID || u.employeeID || u._id || null;
      if (!tcEmployeeNum) { surface('Could not resolve your employee ID — please refresh and try again'); return; }
      console.log('[postTcMessage] tcEmployeeNum:', tcEmployeeNum, 'replacesCommentID:', state.threadBuilder.composing.replacing);

      const replacesCommentID = state.threadBuilder.composing.replacing || null;
      const tempId = -Date.now();

      commit('SET_TC_COMPOSE_ERROR', null);
      commit('SET_TC_COMPOSE_SENDING', true);
      commit('INSERT_OPTIMISTIC_TC_COMMENT', { tempId, body: trimmed, tcEmployeeNum });

      try {
        const response = await axios.post('/post-tc-message', {
          siteID, patNum, treatPlanNum, tcEmployeeNum, body: trimmed,
          replacesCommentID,
        });
        if (!response.data || !response.data.success) {
          throw new Error(response.data && response.data.message ? response.data.message : 'Failed to post reply');
        }
        const data = response.data.data || {};
        commit('REPLACE_OPTIMISTIC_TC_COMMENT', {
          tempId,
          comment: data.comment || null,
          replacedCommentID: data.replacedCommentID || null,
        });
        return data;
      } catch (err) {
        console.error('[postTcMessage] error:', err.response ? err.response.status : 'no-status', err);
        commit('REMOVE_OPTIMISTIC_TC_COMMENT', { tempId });
        commit('SET_TC_COMPOSE_ERROR', err.message || 'Could not send your reply.');
        throw err;
      } finally {
        commit('SET_TC_COMPOSE_SENDING', false);
      }
    },

    /**
     * Slice 8b — patient submits a question.
     *
     * Optimistic update path:
     *   1. Insert a placeholder patient row (negative commentID) so the
     *      message appears in the thread immediately.
     *   2. If AI is enabled, also insert a "Office is replying…" placeholder.
     *   3. POST /post-tx-comment, await the synchronous Bedrock response.
     *   4. Replace placeholders with the real rows from the server.
     *   5. On error, drop placeholders and surface the error in compose state.
     */
    async postPatientComment({ commit, state }, { body }) {
      console.log('[postPatientComment] entered; body length:', (body || '').length);
      const shortId = state.patientView.shortId;
      if (!shortId) throw new Error('No active patient session');
      const trimmed = String(body || '').trim();
      if (!trimmed) throw new Error('Question cannot be empty');

      const aiOn = !!state.threadPatient.aiCommentsEnabled;
      // Slice 8d-1 — only show the "Office is replying…" placeholder when AI
      // will actually surface to the patient on this submit. Review mode
      // means the AI draft is held for office approval, so the patient
      // shouldn't see anything but their own bubble until the office releases.
      const aiVisibleNow = aiOn && state.threadPatient.aiReplyMode !== 'review';
      const tempPatientId = -Date.now();
      const tempAiId = aiVisibleNow ? tempPatientId - 1 : 0;
      console.log('[postPatientComment] aiOn:', aiOn, 'aiVisibleNow:', aiVisibleNow, 'shortId:', shortId, 'tempPatientId:', tempPatientId);

      commit('SET_COMPOSE_ERROR', null);
      commit('SET_COMPOSE_SENDING', true);
      commit('INSERT_OPTIMISTIC_PATIENT_COMMENT', { tempId: tempPatientId, body: trimmed });
      if (aiVisibleNow) commit('INSERT_OPTIMISTIC_AI_PLACEHOLDER', { tempId: tempAiId });

      try {
        console.log('[postPatientComment] POST /post-tx-comment...');
        const response = await axios.post('/post-tx-comment', {
          shortId, body: trimmed,
        }, { crossdomain: true });
        console.log('[postPatientComment] response received; status:', response.status, 'data:', response.data);

        if (!response.data || !response.data.success) {
          throw new Error(response.data && response.data.message ? response.data.message : 'Failed to post comment');
        }
        const data = response.data.data || {};
        console.log('[postPatientComment] applying REPLACE; aiComment present:', !!data.aiComment, 'aiSkipped:', data.aiSkipped);
        commit('REPLACE_OPTIMISTIC_COMMENTS', {
          tempPatientId,
          tempAiId,
          patientComment: data.patientComment || null,
          aiComment: data.aiComment || null,
        });
        return data;
      } catch (err) {
        console.error('[postPatientComment] error:', err.response ? err.response.status : 'no-status', err);
        // extractApiError, not err.message: with an HTTP response axios's message
        // is always "Request failed with status code NNN". The comments master
        // gate 403s with a real sentence ("Your office isn't taking questions
        // through this page right now. Please call the office instead."), and
        // that sentence is the only instruction the patient gets — err.message
        // would replace it with a status code. See src/utils/apiError.js.
        let message = extractApiError(err, 'Something went wrong while sending your question.');
        if (err.response && err.response.status === 429) {
          try {
            const parsed = JSON.parse(err.response.data || '{}');
            if (parsed.reason === 'too-soon') {
              message = `Please wait ${parsed.retryAfterSeconds || 30}s before sending another question.`;
            } else if (parsed.reason === 'daily-cap') {
              message = "You've reached today's question limit. Please try again tomorrow.";
            } else {
              message = 'Slow down a moment and try again.';
            }
          } catch (e) { /* leave default */ }
        }
        commit('REMOVE_OPTIMISTIC_COMMENTS', { tempPatientId, tempAiId });
        commit('SET_COMPOSE_ERROR', message);
        throw err;
      } finally {
        commit('SET_COMPOSE_SENDING', false);
      }
    },
    clearComposeError({ commit }) { commit('SET_COMPOSE_ERROR', null); },

    // ─── Settings actions (Slice 5) ───────────────────

    /**
     * Load all Tx Presenter settings sections for a site. Sections that haven't
     * been saved come back as null — getters apply TX_PRESENTER_DEFAULTS in that
     * case, so callers don't have to special-case unseeded sections.
     *
     * Idempotent — fine to call multiple times. The Builder calls this on
     * bootstrap so the right pane gets practice payment options if configured.
     */
    async loadSettings({ commit, state }, { siteID, force = false } = {}) {
      if (!siteID) throw new Error('siteID is required');
      // Skip if already loaded for this site and not stale (caching across page nav)
      if (!force && state.settings.siteID === siteID && state.settings.lastFetchedAt) {
        const age = Date.now() - state.settings.lastFetchedAt;
        if (age < 60000) return; // 60s cache
      }

      commit('SET_SETTINGS_LOADING', true);
      commit('SET_SETTINGS_ERROR', null);

      try {
        const response = await axios.post('/get-tx-settings', { siteID }, { crossdomain: true });
        if (response.data && response.data.success) {
          commit('SET_SETTINGS_DATA', response.data.data);
          return response.data.data;
        }
        throw new Error(response.data?.message || 'Failed to load settings');
      } catch (err) {
        console.error('[txPresenter] loadSettings error:', err);
        commit('SET_SETTINGS_ERROR', err.message || 'Failed to load settings');
        throw err;
      } finally {
        commit('SET_SETTINGS_LOADING', false);
      }
    },

    /**
     * Save one settings section. Mirrors the per-section "Save" button in the
     * Settings UI — explicit saves only, no autosave. On success the local
     * cache is updated so other surfaces (Builder right pane, preview header)
     * pick up the new values immediately.
     */
    async saveSettingsSection({ commit, state }, { section, value }) {
      const siteID = state.settings.siteID;
      if (!siteID) throw new Error('Settings not loaded — call loadSettings first');
      if (!section) throw new Error('section is required');
      if (value === undefined || value === null) throw new Error('value is required');

      commit('SET_SETTINGS_SAVING_SECTION', section);
      commit('SET_SETTINGS_ERROR', null);

      try {
        const response = await axios.post('/save-tx-settings', {
          siteID, section, value,
        }, { crossdomain: true });
        if (response.data && response.data.success) {
          commit('SET_SETTINGS_SECTION', { section, value });
          return response.data.data;
        }
        throw new Error(response.data?.message || 'Failed to save settings');
      } catch (err) {
        console.error('[txPresenter] saveSettingsSection error:', err);
        // extractApiError so the server's REASON reaches the settings banner.
        // saveTxSettings 400s with "Patient comments can't be turned on until a
        // verified sender email is set up under Settings → Email." — with
        // err.message the office would only ever see "Request failed with status
        // code 400" and have no idea what to fix.
        commit('SET_SETTINGS_ERROR', extractApiError(err, 'Failed to save settings'));
        throw err;
      } finally {
        commit('SET_SETTINGS_SAVING_SECTION', null);
      }
    },

    // ─── Cherry financing connection (cherry-service) ─────────────────
    //
    // These four talk to cherry-service, NOT tx-presenter-service — the Cherry
    // connection is its own thing with its own credentials, and lives outside the
    // TX_PRESENTER_SETTINGS_* blobs (in teamio.settings CHERRY_CONNECTION, written
    // only by the backend so the encrypted key never passes through the browser).
    //
    // They deliberately do NOT go through the settings save/dirty machinery: pasting
    // an API key is a connect action with an immediate pass/fail answer from Cherry,
    // not a form field you edit and Save later. Nothing is stored unless Cherry
    // accepts the key, so there is no "saved but invalid" state to represent.
    //
    // No response from any of these ever contains key material — the backend returns
    // an explicit allow-list (see cherry-service/lib/connection-store.js publicView).

    /** What Cherry account, if any, is attached to this site. Makes no Cherry call. */
    async loadCherryConnection(_ctx, { siteID }) {
      if (!siteID) throw new Error('siteID is required');
      const response = await axios.post('/cherry/get-connection', { siteID }, { crossdomain: true });
      if (response.data && response.data.success) return response.data.data;
      throw new Error(response.data?.message || 'Failed to load the Cherry connection');
    },

    /**
     * Validate a pasted Business API Key with Cherry and, only if Cherry accepts it,
     * store it. Returns the same public shape as loadCherryConnection.
     */
    async connectCherry(_ctx, { siteID, businessApiKey }) {
      if (!siteID) throw new Error('siteID is required');
      if (!businessApiKey) throw new Error('businessApiKey is required');
      const response = await axios.post('/cherry/connect', { siteID, businessApiKey }, { crossdomain: true });
      if (response.data && response.data.success) return response.data.data;
      throw new Error(response.data?.message || 'Cherry did not accept that key');
    },

    /** Pick which of the business's practices this site maps to. */
    async selectCherryPractice(_ctx, { siteID, practiceId }) {
      if (!siteID) throw new Error('siteID is required');
      if (!practiceId) throw new Error('practiceId is required');
      const response = await axios.post('/cherry/select-practice', { siteID, practiceId }, { crossdomain: true });
      if (response.data && response.data.success) return response.data.data;
      throw new Error(response.data?.message || 'Failed to select the practice');
    },

    /**
     * Build (or rebuild) the practice's Cherry PLAN CATALOG - "which plans does my Cherry account
     * actually offer" - and return it: `{ plans, ladder, fetchedAt }`.
     *
     * Cherry has no list-my-plans endpoint, so cherry-service prices a fixed ladder of amounts and
     * merges the answers. That is 12 GETs per call, so this runs only on an explicit "Load my
     * Cherry plans" / "Refresh from Cherry" click or when Settings opens on a catalog older than
     * a week - never from a render path. It sends dollar amounts to Cherry and NO patient data (the
     * request carries only `siteID` and the opt-in flag), so like fetchCherryPlanTerms it needs no
     * consent step.
     *
     * There is NO route of its own: it is the get-connection endpoint with the opt-in body flag
     * `refreshCatalog: true` (the shared API Gateway is at its resource limit, so a new path could not
     * deploy). Without the flag get-connection is a plain DB read that never calls Cherry - that is
     * `loadCherryConnection` above; this action is the only caller that passes the flag. The success
     * body is the normal connection shape with the FRESH catalog under `catalog`, and this resolves
     * to that catalog alone - `{ plans, ladder, fetchedAt }` - so the caller keeps working as before.
     *
     * `plans[]` = `{ key, term, periodLength, aprMin, aprMax, minAmount, maxAmount }`, where `key`
     * is `${term}-${PERIOD}` exactly as utils/cherryTerms.js cherryPlanKey builds it. The backend
     * stores the result itself (teamio.settings CHERRY_PLAN_CATALOG) and get-connection returns it
     * as `catalog`, so nothing is committed to the store - the caller (Settings) holds it.
     *
     * Same envelope as the other cherry actions: `response.data.success` -> `response.data.data`.
     * A success that carries no catalog object is treated as a failure, so the caller keeps the
     * catalog it already shows instead of blanking it. Errors are bare strings (handler-kit), 403
     * when the site is not entitled, 400 when Cherry is not connected / no location is chosen:
     * callers read them with extractApiError(err, ...).
     */
    async fetchCherryPlanCatalog(_ctx, { siteID }) {
      if (!siteID) throw new Error('siteID is required');
      const response = await axios.post('/cherry/get-connection', { siteID, refreshCatalog: true }, { crossdomain: true });
      if (response.data && response.data.success) {
        const catalog = response.data.data && response.data.data.catalog;
        if (catalog && typeof catalog === 'object') return catalog;
      }
      throw new Error(response.data?.message || 'Could not load your Cherry plans');
    },

    /**
     * Price this patient's portion against the practice's REAL Cherry pricing, and store the
     * result server-side against the presentation.
     *
     * ⛔ SENDS NO PATIENT DATA. Cherry's /v1/payment-plan-estimates prices from the BUSINESS's
     * configuration and takes no patient — `patNum`/`treatPlanNum` never leave Teamio, they are
     * only the key the answer is filed under. That is what lets this run without a consent
     * step, and cherry-service/tests/no-autonomous-patient-actions.test.js keeps it true.
     *
     * The terms are written by the BACKEND, in the same request that fetched them — never
     * posted up from here. Credit terms a caller could supply are credit terms a caller could
     * forge, onto a document a patient signs.
     *
     * An empty `plans` array is a SUCCESS: Cherry legitimately prices nothing at some amounts.
     * Callers must render no figures, never the practice's typed terms.
     *
     * Commits `{ purchaseAmount, plans, fetchedAt }` to builder.cherryTerms and returns it. A
     * FAILURE commits null - "no terms", i.e. no figures - and is logged, NOT thrown: a Cherry
     * outage must not break the Builder or Present Mode, and a thrown error from a background
     * re-price (nobody is waiting on it) would only be an unhandled rejection. Only a missing
     * argument throws, because that is a bug in the caller.
     *
     * Latest wins, and only for the plan still open: a slow answer for an amount the TC has
     * since moved on from, or for a plan they have since left, is dropped rather than committed.
     */
    async fetchCherryPlanTerms({ commit, state }, { siteID, patNum, treatPlanNum, purchaseAmount }) {
      if (!siteID) throw new Error('siteID is required');
      if (!patNum || !treatPlanNum) throw new Error('patNum and treatPlanNum are required');

      const seq = ++cherryTermsFetchSeq;
      const stillCurrent = () => seq === cherryTermsFetchSeq
        && state.builder.treatPlanNum === treatPlanNum
        && state.builder.patNum === patNum;

      commit('SET_BUILDER_CHERRY_TERMS_LOADING', true);
      try {
        const response = await axios.post('/cherry/plan-terms', {
          siteID, patNum, treatPlanNum, purchaseAmount,
        }, { crossdomain: true });
        if (!(response.data && response.data.success)) {
          throw new Error(response.data?.message || 'Could not get financing terms from Cherry');
        }
        const d = response.data.data || {};
        const terms = {
          purchaseAmount: d.purchaseAmount,
          plans: Array.isArray(d.plans) ? d.plans : [],
          fetchedAt: d.fetchedAt || null,
        };
        if (stillCurrent()) commit('SET_BUILDER_CHERRY_TERMS', terms);
        return terms;
      } catch (err) {
        console.error('[txPresenter] fetchCherryPlanTerms error:', err);
        if (stillCurrent()) commit('SET_BUILDER_CHERRY_TERMS', null);
        return null;
      } finally {
        if (seq === cherryTermsFetchSeq) commit('SET_BUILDER_CHERRY_TERMS_LOADING', false);
      }
    },

    /** Detach. Does nothing on Cherry's side — the key belongs to the practice. */
    async disconnectCherry(_ctx, { siteID }) {
      if (!siteID) throw new Error('siteID is required');
      const response = await axios.post('/cherry/disconnect', { siteID }, { crossdomain: true });
      if (response.data && response.data.success) return response.data.data;
      throw new Error(response.data?.message || 'Failed to disconnect');
    },
  },
};
